// =============================================================================
// ProgramPanel — the build program on a project (Gantt P1, mig 342, 2 Sep 2026)
// =============================================================================
// James: "I just want an interactive clean way to build and manage the plans
// and clean sending to the client when things change, as they do, a lot."
//
// P1 = the plumbing his Excel + VBA macros used to be:
//   * Import a coretel.program.v1 payload (paste from his Projects Claude via
//     the Build Program directive, or upload his v3.11-style Gantt workbook —
//     the Schedule sheet parses directly).
//   * DIFF REVIEW before anything applies: new lines, date changes (old → new),
//     removed lines, and which existing bookings sit inside changed windows.
//   * Apply = one atomic call to app.program_apply (revision stored with the
//     raw payload; milestone-kind rows sync to project_formal_milestones).
//   * Copy JSON = app.program_export, the round-trip for "send it back to
//     Claude / the client".
// The drag-and-drop Gantt editor is P2; Excel/PDF exports in his exact client
// format are P3. Bookings are never auto-created here — timestamps need eyes.
//
// lane ≠ owner (his 2 Sep answer): lane is the scope swimlane (per-project,
// defaults CIVILS/STRUCTURE/ICIO/POWER/ACCEPT); owner is who does it —
// internal / client:Hardline / subbie:UES. Client & subbie lines render as
// outlined "we work around these" rows.
// =============================================================================

import React, { useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CalendarRange, Upload, Clipboard, Copy, Check, X, ChevronRight, ChevronDown,
  FileSpreadsheet, Diamond, AlertTriangle, List, Plus, Trash2, ArrowUp, ArrowDown,
  Truck, Zap, Link2, Unlink, FileText, HardHat, Eye, Send, Moon,
  Car, Coffee, BadgeCheck, BadgeX, Clock, GripVertical,
} from "lucide-react";
// programExcelExport (and the exceljs library inside it) loads on demand when
// the Excel button is clicked — statically it adds ~1 MB to the main bundle,
// which pushed the PWA precache over its limit and failed the 2 Sep deploy.
import * as XLSX from "xlsx";
import { supabase as supabaseTyped } from "@/lib/supabase";
// Program tables post-date the generated types (regen queued) — same cast as
// the design tab uses.
const supabase = supabaseTyped as any;
import { createProjectBooking, rescheduleProjectBooking } from "@/lib/bookingWrites";   // 3 Nov 2026 — one way in for a booking
import { Card, Button, Spinner, Textarea, Input, Pill } from "@/components/ui";

// ---------------------------------------------------------------------------
// Types (local — tables newer than the generated types)
// ---------------------------------------------------------------------------
type ProgramActivity = {
  activity_id: string;
  code: string;
  wbs: string | null;
  lane: string | null;
  owner: string;
  name: string;
  resource: string | null;
  // mig 371 — acceptance (a measured window on OUR delivered work, pass/fail),
  // travel (windscreen time) and standdown (paid, committed, no progress):
  // the three a remote programme is mostly made of.
  kind: "activity" | "milestone" | "outage" | "plant" | "hold" | "client_deliverable"
      | "acceptance" | "travel" | "standdown";
  acceptance_result: "pass" | "fail" | null;
  start_date: string | null;
  days: number | null;
  end_date: string | null;
  depends_on: string | null;
  client_visible: boolean;
  notes: string | null;
  sort_order: number;
  is_active: boolean;
  milestone_id: string | null;
  booking_id: string | null;
  plant_needed: string[];
  is_key: boolean;
  in_build_plan: boolean;
  // mig 358 — line progress. done = manager marked the line complete;
  // track_progress off = this line carries no progress (no shading, no badge).
  track_progress: boolean;
  done: boolean;
  done_at: string | null;
  // mig 366 — facts about the work the chart used to have to guess at.
  // night_shift: the work crosses midnight, so the bar straddles the day
  // boundary instead of sitting inside one column looking like a day task.
  // outage_impact: 'nsa' routine (quiet), 'sa' takes service (red, shades the
  // row). Null on an outage is read as 'sa' — loud is the safe default.
  night_shift: boolean;
  outage_impact: "nsa" | "sa" | null;
  // mig 368 — work performed INSIDE a service-affecting window: the minutes the
  // network is actually down. Not the window itself (that's kind 'outage').
  under_outage: boolean;
};

/** The one colour in the chart that means "customers are off air while this
 *  happens". Deep emerald — picked by measurement against every hue already in
 *  use (worst-case deutan ΔE 12.6, the nearest being the POWER lane's mint). */
const UNDER_OUTAGE = "#12805c";
/** mig 371 — measured against every hue already in use with the palette
 *  validator. ACCEPTANCE clears the normal-vision floor (ΔE 15.8) and the CVD
 *  floor (9.3); TRAVEL clears both comfortably (18.9 / 12.5). STAND-DOWN has
 *  NO hue: nothing left in the space cleared ΔE 15 against the night indigo,
 *  so it shares travel's brown at reduced weight and is told apart by its chip.
 *  Past twelve meanings, hue runs out and form has to carry it. */
const ACCEPTANCE = "#7c3aed";
const TRAVEL     = "#78350f";
const STANDDOWN_BG = "repeating-linear-gradient(135deg, rgba(120,53,15,.34), rgba(120,53,15,.34) 6px, rgba(120,53,15,.13) 6px, rgba(120,53,15,.13) 12px)";

/** An outage with nothing set reads as service-affecting; an older row whose
 *  name says NSA is believed. (mig 366 backfills the column, this is the belt
 *  for anything imported before it or typed in by hand.) */
function outageImpact(a: { kind: string; name: string; outage_impact?: string | null }): "nsa" | "sa" | null {
  if (a.kind !== "outage") return null;
  if (a.outage_impact === "nsa" || a.outage_impact === "sa") return a.outage_impact;
  return /(^|[^a-z])nsa([^a-z]|$)|non[- ]service/i.test(a.name) ? "nsa" : "sa";
}

// Progress is DERIVED, never stored (mig 358): a done line is 100%, else the
// sub-activity ticks tell the story, else 0. Lines with track_progress off
// return null — no shading, no badge, no claim.
function progressPct(a: ProgramActivity, subs: SubRow[]): number | null {
  if (!a.track_progress) return null;
  if (a.done) return 100;
  if (subs.length) return Math.round((subs.filter((s) => s.done).length / subs.length) * 100);
  return 0;
}
type SubCrew = "riggers" | "tech" | "civil" | null;
type SubRow = {
  sub_id: string; activity_id: string; name: string; sort_order: number;
  done: boolean; done_at: string | null;
  crew: SubCrew;   // mig 381 — which crew does this step; null = the line's lane decides
};

// mig 381 — the crew rides on the wire as a name prefix ("Riggers: …"), the
// same convention the Projects Claude already uses. These two mirror
// app.program_sub_crew_split / _prefix so the editor speaks the RPC's language.
const CREW_LABEL: Record<Exclude<SubCrew, null>, string> = { riggers: "Riggers", tech: "Tech", civil: "Civil" };
const CREW_SHORT: Record<Exclude<SubCrew, null>, string> = { riggers: "R", tech: "T", civil: "C" };
function splitSubCrew(raw: string): { crew: SubCrew | "" ; name: string } {
  const m = raw.match(/^\s*([A-Za-z]+)\s*:\s*(.+)$/);
  if (!m) return { crew: null, name: raw.trim() };
  const w = m[1].toLowerCase();
  if (["riggers", "rigger", "rigging"].includes(w)) return { crew: "riggers", name: m[2].trim() };
  if (["tech", "techs", "technician", "technicians"].includes(w)) return { crew: "tech", name: m[2].trim() };
  if (["civil", "civils"].includes(w)) return { crew: "civil", name: m[2].trim() };
  if (["any", "both", "none"].includes(w)) return { crew: "", name: m[2].trim() };
  return { crew: null, name: raw.trim() };
}
/** what the editor sends: an explicit "Any:" so a cleared chip actually clears the row */
const encodeSub = (name: string, crew: SubCrew) => `${crew ? CREW_LABEL[crew] : "Any"}: ${name}`;
type BookingRow = {
  booking_id: string; title: string; booking_type: string; status: string;
  scheduled_start: string; scheduled_end: string | null; outage_approved: boolean;
};
type ProgramLane = { lane_code: string; label: string | null; color: string | null; sort_order: number };
type ProgramRevision = {
  revision_id: string; revision_no: number; revision_label: string | null;
  source: string; created_at: string; summary: any;
  // What the exported PDF and workbook print as "Status". It has existed on the
  // revision row since mig 342 but nothing ever read it, so every export said
  // "Draft" — the word was hard-coded as the fallback and nothing set the value.
  issue_status: string | null;
};
type PayloadActivity = {
  id: string; wbs?: string | null; lane?: string | null; owner?: string | null;
  name: string; resource?: string | null; kind?: string | null;
  start?: string | null; days?: number | null; end?: string | null;
  depends_on?: string | null; client_visible?: boolean | null; notes?: string | null;
  plant?: string[] | null;
  // mig 361 / 365 — carried by the payload, absent means "leave what Ops has".
  // `subs` being UNDEFINED (not []) is what lets stale tick-boxes survive onto a
  // reused id, which is why the import review checks for it.
  subs?: string[] | null;
  done?: boolean | null; track_progress?: boolean | null;
  // mig 366
  night?: boolean | null; impact?: "nsa" | "sa" | null;
  // mig 368 — the work that only happens because the network is down. Absent
  // means "leave what Ops has", so a tick James made survives a re-import.
  under_outage?: boolean | null;
};
type ProgramPayload = {
  schema: string;
  project?: { code?: string; revision?: string; issue_status?: string; audience?: string; lanes?: string[] };
  activities: PayloadActivity[];
};

const DEFAULT_LANE_COLORS: Record<string, string> = {
  CIVILS: "#4f97d7", STRUCTURE: "#7a8aa0", ICIO: "#e2661a", POWER: "#37a97f", ACCEPT: "#9b7bd4",
};

/** owner strings: internal | client:<Name> | subbie:<Name> | external:<Name> (unclassified) */
function parseOwner(owner: string): { type: "internal" | "client" | "subbie" | "external"; name: string } {
  if (owner === "internal") return { type: "internal", name: "Coretel" };
  const m = owner.match(/^(client|subbie|external):(.*)$/);
  if (m) return { type: m[1] as any, name: m[2] || "External" };
  return { type: "external", name: owner };
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------
/** Local-time day key for a timestamptz — slicing the UTC string buckets a
 *  7am AEST booking onto the previous day (same bug fixed on the schedule). */
function tsDay(ts: string): string {
  const d = new Date(ts);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function fmtDate(d: string | null | undefined): string {
  if (!d) return "—";
  const dt = new Date(d + "T00:00:00");
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
}

/** Tolerant JSON extraction — same trick as the design paste: accepts a bare
 *  object, or one buried in a chat reply / ```json fence. */
function extractProgramJson(text: string): ProgramPayload | null {
  const tryParse = (s: string): ProgramPayload | null => {
    try {
      const v = JSON.parse(s);
      if (v && typeof v === "object" && v.schema === "coretel.program.v1" && Array.isArray(v.activities)) return v;
    } catch { /* keep looking */ }
    return null;
  };
  const direct = tryParse(text.trim());
  if (direct) return direct;
  const fence = text.match(/```(?:json)?\s*([\s\S]*?)```/);
  if (fence) { const v = tryParse(fence[1].trim()); if (v) return v; }
  const start = text.indexOf('{');
  if (start >= 0) {
    // walk to the matching close brace of the first object
    let depth = 0;
    for (let i = start; i < text.length; i++) {
      if (text[i] === '{') depth++;
      else if (text[i] === '}') { depth--; if (depth === 0) { const v = tryParse(text.slice(start, i + 1)); if (v) return v; break; } }
    }
  }
  return null;
}

/** Spot plant in free text — "CRANE DUTY 2 + EWP", "Borger", "Franna"… */
function inferPlant(...texts: (string | null | undefined)[]): string[] {
  const hay = texts.filter(Boolean).join(" · ").toLowerCase();
  const out: string[] = [];
  if (/crane|borger|classic cp|crane duty/.test(hay)) out.push("Crane");
  if (/ewp|elevated work|boom lift|knuckle/.test(hay)) out.push("EWP");
  if (/franna/.test(hay)) out.push("Franna");
  if (/excavat/.test(hay)) out.push("Excavator");
  if (/concrete pump|line pump|boom pump/.test(hay)) out.push("Concrete pump");
  return out;
}

/** Excel serials / dd-mm-yyyy / Date cells → ISO, or null. Never throws. */
function cellToIso(v: any): string | null {
  if (v == null || v === "") return null;
  if (v instanceof Date && !isNaN(v.getTime())) {
    return `${v.getFullYear()}-${String(v.getMonth() + 1).padStart(2, "0")}-${String(v.getDate()).padStart(2, "0")}`;
  }
  if (typeof v === "number" && v > 20000 && v < 80000) {
    const d = XLSX.SSF.parse_date_code(v);
    if (d) return `${d.y}-${String(d.m).padStart(2, "0")}-${String(d.d).padStart(2, "0")}`;
  }
  const s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const dm = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
  if (dm) {
    const y = dm[3].length === 2 ? `20${dm[3]}` : dm[3];
    return `${y}-${dm[2].padStart(2, "0")}-${dm[1].padStart(2, "0")}`;
  }
  // "Mon 10-Aug-26" / "10-Aug-2026" style
  const parsed = new Date(s.replace(/^[A-Za-z]{3}\s+/, ""));
  if (!isNaN(parsed.getTime()) && parsed.getFullYear() > 2000) {
    return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}-${String(parsed.getDate()).padStart(2, "0")}`;
  }
  return null;
}

/** Parse James's v3.11-style Gantt workbook: the Schedule sheet, headers on
 *  the row containing "#"+"Task", data below. Falls back to the Summary sheet
 *  (same columns, no grid). */
function workbookToPayload(wb: XLSX.WorkBook): ProgramPayload | null {
  const ws = wb.Sheets["Schedule"] ?? wb.Sheets["Summary"];
  if (!ws) return null;
  const grid: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, defval: "" });
  // find the header row: contains both "#"-ish first col and a "Task" cell
  let hr = -1;
  for (let i = 0; i < Math.min(grid.length, 12); i++) {
    const cells = grid[i].map((c) => String(c).trim().toLowerCase());
    if (cells.includes("task") && (cells.includes("wbs") || cells.includes("lane") || cells.includes("start"))) { hr = i; break; }
  }
  if (hr < 0) return null;
  const heads = grid[hr].map((c) => String(c).trim().toLowerCase());
  const col = (name: string) => heads.indexOf(name);
  const ci = {
    site: col("site"), lane: col("lane"), wbs: col("wbs"), task: col("task"),
    resource: col("resource"), start: col("start"), finish: col("finish"),
    days: col("days"), notes: col("notes"),
  };
  const acts: PayloadActivity[] = [];
  for (let r = hr + 1; r < grid.length; r++) {
    const row = grid[r];
    const name = ci.task >= 0 ? String(row[ci.task] ?? "").trim() : "";
    if (!name) continue;
    const lane = ci.lane >= 0 ? String(row[ci.lane] ?? "").trim().toUpperCase() || null : null;
    const wbs = ci.wbs >= 0 ? String(row[ci.wbs] ?? "").trim() || null : null;
    const start = ci.start >= 0 ? cellToIso(row[ci.start]) : null;
    const end = ci.finish >= 0 ? cellToIso(row[ci.finish]) : null;
    const daysRaw = ci.days >= 0 ? Number(row[ci.days]) : NaN;
    const nm = name.toLowerCase();
    const kind =
      /curing|no works|no site works|hold/.test(nm) ? "hold" :
      /outage/.test(nm) ? "outage" :
      lane === "ACCEPT" || /rfs|accepted|acceptance|hop pack/.test(nm) ? "milestone" :
      "activity";
    const resource = ci.resource >= 0 ? String(row[ci.resource] ?? "").trim() || null : null;
    // The workbook has no owner column, but Resource says who's on the tools:
    // "Coretel + Borger" is ours; "HLG + Nyanda" is not. Whether an external
    // party is a client or a subbie isn't in the sheet — the Who chip in the
    // table is click-to-fix, and re-imports keep a manual choice.
    const rl = (resource ?? "").toLowerCase();
    // holds/outages aren't anyone's line, and "Access hold"-style resources
    // are windows, not companies
    const owner = !resource || rl.includes("coretel") || kind === "hold" || kind === "outage" || /hold/.test(rl)
      ? "internal"
      : `external:${resource.split(/[+\/,]/)[0].trim()}`;
    acts.push({
      id: `${lane || "ACT"}-${wbs || String(acts.length + 1)}`,
      wbs, lane, name, resource,
      plant: inferPlant(name, resource, ci.notes >= 0 ? String(row[ci.notes] ?? "") : null),
      kind, start, end,
      days: Number.isFinite(daysRaw) && daysRaw > 0 ? Math.round(daysRaw) : null,
      notes: ci.notes >= 0 ? String(row[ci.notes] ?? "").trim() || null : null,
      owner,
      client_visible: true,
    });
  }
  if (!acts.length) return null;
  return { schema: "coretel.program.v1", project: {}, activities: acts };
}

// ---------------------------------------------------------------------------
// Diff — computed in the browser so the review shows BEFORE anything applies
// ---------------------------------------------------------------------------
type DiffLine =
  | { type: "new"; act: PayloadActivity }
  | { type: "removed"; act: ProgramActivity }
  | { type: "changed"; act: PayloadActivity; before: ProgramActivity; changes: string[] };

function diffProgram(current: ProgramActivity[], payload: ProgramPayload): { lines: DiffLine[]; unchanged: number } {
  const cur = new Map(current.filter((a) => a.is_active).map((a) => [a.code, a]));
  const incoming = new Set<string>();
  const lines: DiffLine[] = [];
  let unchanged = 0;
  for (const act of payload.activities) {
    incoming.add(act.id);
    const before = cur.get(act.id);
    if (!before) { lines.push({ type: "new", act }); continue; }
    const changes: string[] = [];
    const endFromDays = act.end ?? (act.start && act.days ? addDays(act.start, act.days - 1) : null);
    if ((act.start ?? null) !== before.start_date) changes.push(`start ${fmtDate(before.start_date)} → ${fmtDate(act.start)}`);
    if ((endFromDays ?? null) !== before.end_date) changes.push(`end ${fmtDate(before.end_date)} → ${fmtDate(endFromDays)}`);
    if (act.name.trim() !== before.name) changes.push("renamed");
    if ((act.kind ?? "activity") !== before.kind) changes.push(`type ${before.kind} → ${act.kind ?? "activity"}`);
    if ((act.lane?.toUpperCase() ?? null) !== before.lane) changes.push(`lane → ${act.lane ?? "—"}`);
    if ((act.owner ?? "internal") !== before.owner) changes.push(`owner → ${act.owner ?? "internal"}`);
    if ((act.resource ?? null) !== before.resource) changes.push("resource");
    if ((act.notes ?? null) !== before.notes) changes.push("notes");
    if (changes.length) lines.push({ type: "changed", act, before, changes });
    else unchanged++;
  }
  for (const a of current) {
    if (a.is_active && !incoming.has(a.code)) lines.push({ type: "removed", act: a });
  }
  return { lines, unchanged };
}

// ---------------------------------------------------------------------------
// Stale-subs hazard — the one import fault that reaches a crew's phone
// ---------------------------------------------------------------------------
// James, 13 Sep 2026 (Sharon_R008 rev 4): a milestone imported with NO subs key
// rendered 0/7, carrying seven tick-boxes from whatever activity last held that
// id. Re-laning the program had forced every id to change and `M2-3.1` came
// back on a different line. Sub-activities also pull a line onto the field
// build plan (mig 345/348), so those boxes were live in Today's Work and My
// Schedule — a rigger would have been ticking off someone else's day.
//
// The importer is behaving exactly as documented ("omit the key to leave subs
// untouched"), so this isn't a fix in the RPC — it's a thing the human has to
// see BEFORE pressing Apply.
type SubsHazard = { code: string; name: string; wasName: string; kind: string; subCount: number; milestone: boolean };

function staleSubsHazards(
  lines: DiffLine[],
  subsByActivity: Map<string, SubRow[]>,
  payload: ProgramPayload,
): SubsHazard[] {
  const incoming = new Map(payload.activities.map((a) => [a.id, a]));
  const out: SubsHazard[] = [];
  for (const line of lines) {
    if (line.type !== "changed") continue;
    const inc = incoming.get(line.act.id);
    // The payload speaking about subs (even "subs": []) is safe — it wins.
    if (!inc || Array.isArray(inc.subs)) continue;
    const held = subsByActivity.get(line.before.activity_id) ?? [];
    if (!held.length) continue;
    const kind = inc.kind ?? "activity";
    const renamed = line.changes.some((c) => c === "renamed" || c.startsWith("type "));
    const milestone = kind === "milestone" || kind === "client_deliverable" || kind === "hold";
    // Either the id now holds a materially different activity, or it holds a
    // kind that should never carry crew tick-boxes at all.
    if (!renamed && !milestone) continue;
    out.push({
      code: line.act.id, name: line.act.name, wasName: line.before.name,
      kind, subCount: held.length, milestone,
    });
  }
  return out;
}

function addDays(iso: string, n: number): string {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// ---------------------------------------------------------------------------
// The panel
// ---------------------------------------------------------------------------
/** mig 369 — the program hangs off a PROJECT or a BID. Same shape DesignPanel
 *  uses for the same reason (mig 339): a job gets planned before it's won. */
export type ProgramParent =
  | { kind: "project"; id: string; code: string | null; name: string }
  | { kind: "bid"; id: string; code: string | null; name: string };

export default function ProgramPanel({ parent, preparedBy, siteLine }: {
  parent: ProgramParent;
  preparedBy?: string; siteLine?: string | null;
}) {
  // One parent, two possible columns. `parentCol` is the only thing that
  // changes in every query below; everything else is identical, which is the
  // whole reason the program could move to bids without a second screen.
  const isBid = parent.kind === "bid";
  const parentCol = isBid ? "bid_id" : "project_id";
  const parentKind = parent.kind;       // part of every cache key, so a bid's
                                        // program and a project's never share one
  const projectId = parent.id;          // the id, whichever parent it belongs to
  const projectCode = parent.code;
  const projectName = parent.name;
  const qc = useQueryClient();
  const [importOpen, setImportOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  // Default ON when the chart would actually clip something — the Notes column
  // holds ~34 characters, so anything past that is invisible on the chart.
  const [notesAppendix, setNotesAppendix] = useState(true);
  // Weekly build plan: which lane's work sits on the Riggers row vs the Technician
  // row. Seeded by keyword, then it's James's call — the export never guesses.
  // mig 381 — where UNTAGGED build-plan work prints. No per-lane switch: a lane
  // is not a crew (James, 19 Sep). Default "both" = everyone's.
  const [planUntagged, setPlanUntagged] = useState<"external" | "internal" | "both">("both");
  const [copied, setCopied] = useState(false);
  const [view, setView] = useState<"gantt" | "table">("gantt");
  // James, 13 Sep 2026: "a program we cannot preview as the client is a program
  // we cannot safely send." Internal shows everything; Client drops
  // client_visible=false lines from the on-screen chart AND both exports, so
  // what you see before you hit PDF is what they get.
  const [audience, setAudience] = useState<"internal" | "client">("internal");
  const [editAct, setEditAct] = useState<ProgramActivity | "new" | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["program", parent.kind, projectId],
    queryFn: async () => {
      // Bookings and crew windows are PROJECT things. A bid has neither — no
      // booking to link a line to, no crew assigned to a job we haven't won —
      // so those two legs are skipped rather than queried against a column
      // that would just come back empty.
      const empty = Promise.resolve({ data: [] as any[], error: null as any });
      const [acts, lanes, revs, bks, pcas, subs] = await Promise.all([
        supabase.from("project_program_activities").select("*")
          .eq(parentCol, projectId).order("sort_order"),
        supabase.from("project_program_lanes").select("*")
          .eq(parentCol, projectId).order("sort_order"),
        supabase.from("project_program_revisions")
          .select("revision_id, revision_no, revision_label, source, created_at, summary, issue_status")
          .eq(parentCol, projectId).order("revision_no", { ascending: false }).limit(5),
        isBid ? empty : supabase.from("project_bookings")
          .select("booking_id, title, booking_type, status, scheduled_start, scheduled_end, outage_approved")
          .eq("project_id", projectId).neq("status", "cancelled"),
        isBid ? empty : supabase.from("project_crew_assignments")
          .select("assigned_from, assigned_to")
          .eq("project_id", projectId),
        supabase.from("project_program_subactivities")
          .select("sub_id, activity_id, name, sort_order, done, done_at, crew")
          .eq(parentCol, projectId).order("sort_order"),
      ]);
      if (acts.error) throw acts.error;
      if (lanes.error) throw lanes.error;
      if (revs.error) throw revs.error;
      if (bks.error) throw bks.error;
      if (pcas.error) throw pcas.error;
      if (subs.error) throw subs.error;
      return {
        activities: (acts.data ?? []) as ProgramActivity[],
        lanes: (lanes.data ?? []) as ProgramLane[],
        revisions: (revs.data ?? []) as ProgramRevision[],
        bookings: (bks.data ?? []) as BookingRow[],
        crewWindows: (pcas.data ?? []) as { assigned_from: string; assigned_to: string | null }[],
        subs: (subs.data ?? []) as SubRow[],
      };
    },
  });

  // Everything live on the program, regardless of who's looking.
  const activeAll = useMemo(() => (data?.activities ?? []).filter((a) => a.is_active), [data]);
  // What the current audience sees — drives the chart, the table AND both
  // exports, so the preview and the sent document can never disagree.
  const active = useMemo(
    () => (audience === "client" ? activeAll.filter((a) => a.client_visible !== false) : activeAll),
    [activeAll, audience],
  );
  const hiddenFromClient = activeAll.length - active.length;
  const laneColor = (lane: string | null): string => {
    if (!lane) return "#8a95a3";
    const row = data?.lanes.find((l) => l.lane_code === lane);
    return row?.color || DEFAULT_LANE_COLORS[lane] || "#8a95a3";
  };

  // Group active activities by lane, in lane order, unknown lanes last
  const grouped = useMemo(() => {
    const order = new Map((data?.lanes ?? []).map((l, i) => [l.lane_code, i]));
    const byLane = new Map<string, ProgramActivity[]>();
    for (const a of active) {
      const key = a.lane ?? "OTHER";
      if (!byLane.has(key)) byLane.set(key, []);
      byLane.get(key)!.push(a);
    }
    return [...byLane.entries()].sort((x, y) =>
      (order.get(x[0]) ?? 99) - (order.get(y[0]) ?? 99));
  }, [active, data]);

  const latestRev = data?.revisions?.[0] ?? null;

  // mig 367 — every milestone and client deliverable used to arrive starred
  // whether the payload asked or not, so a program landed with nine "key dates"
  // nobody chose. The importer no longer does that, but the rows it already
  // starred are still there and the migration deliberately doesn't mass-clear
  // them (it can't tell one James chose from one it invented). This does it in
  // one click, scoped to this project, with his hand on it.
  const keyCount = activeAll.filter((a) => a.is_key).length;
  /** James, 13 Sep: "what drives the status of the PDF (it's in draft at the
   *  moment)". Nothing did — the exports passed null and the PDF fell back to a
   *  hard-coded "Draft", so a programme issued to a Tier 1 still printed Draft.
   *  It's the manager's call when a document stops being a draft, so it's a
   *  control here rather than something the payload asserts. Stored on the
   *  latest revision, which is where mig 342 always intended it to live. */
  const ISSUE_STATUSES = ["Draft", "For review", "Issued for construction", "Superseded"] as const;
  const issueStatus = latestRev?.issue_status?.trim() || "Draft";
  async function setIssueStatus(next: string) {
    if (!latestRev) return;
    const { error } = await supabase.from("project_program_revisions")
      .update({ issue_status: next })
      .eq("revision_id", latestRev.revision_id);
    if (error) { alert(`Couldn't set the status: ${error.message}`); return; }
    qc.invalidateQueries({ queryKey: ["program", parentKind, projectId] });
  }

  async function clearKeyDates() {
    if (!window.confirm(
      `Clear the key-date flag from all ${keyCount} flagged ${keyCount === 1 ? "line" : "lines"} on this program?\n\n` +
      `Nothing else changes — dates, subs and progress all stay. You can re-flag the ones that really are key with the star on each row.`,
    )) return;
    const { error } = await supabase
      .from("project_program_activities")
      .update({ is_key: false, updated_at: new Date().toISOString() })
      .eq(parentCol, projectId).eq("is_active", true).eq("is_key", true);
    if (error) { alert(`Couldn't clear: ${error.message}`); return; }
    qc.invalidateQueries({ queryKey: ["program", parentKind, projectId] });
  }

  async function copyJson() {
    const { data: payload, error } = await supabase.schema("app").rpc("program_export",
      isBid ? { p_bid_id: projectId } : { p_project_id: projectId });
    if (error) { alert(`Export failed: ${error.message}`); return; }
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function downloadExcel() {
    try {
      const { exportProgramXlsx } = await import("./programExcelExport");
      await exportProgramXlsx({
        projectName,
        projectCode,
        siteLine: siteLine ?? null,
        revisionLabel: latestRev?.revision_label ?? null,
        revisionNo: latestRev?.revision_no ?? null,
        issueStatus,
        audience: audience === "client" ? "Client" : "Internal",
        preparedBy: preparedBy || "Coretel Group",
        activities: active,
        lanes: data?.lanes ?? [],
        clientVisibleOnly: audience === "client",
      });
    } catch (e: any) {
      alert(e?.message ?? "Export failed");
    }
  }

  async function downloadBuildPlan() {
    try {
      const { exportBuildPlan } = await import("./buildPlanExcel");
      await exportBuildPlan({
        projectName,
        projectCode,
        siteLine: siteLine ?? null,
        revisionLabel: latestRev?.revision_label ?? null,
        revisionNo: latestRev?.revision_no ?? null,
        preparedBy: preparedBy || "Coretel Group",
        untagged: planUntagged,
        clientVisibleOnly: audience === "client",
        activities: active.map((a) => ({
          ...a,
          subs: (subsByActivity.get(a.activity_id) ?? []).map((s: any) => ({ name: s.name as string, crew: (s.crew ?? null) as SubRow["crew"] })),
        })),
      });
    } catch (e: any) {
      alert(e?.message ?? "Build plan export failed");
    }
  }

  async function downloadPdf() {
    try {
      const { exportProgramPdf } = await import("./programPdfExport");
      await exportProgramPdf({
        projectName,
        projectCode,
        siteLine: siteLine ?? null,
        revisionLabel: latestRev?.revision_label ?? null,
        revisionNo: latestRev?.revision_no ?? null,
        issueStatus,
        preparedBy: preparedBy || "Coretel Group",
        // mig 358 — carry derived progress so the client PDF shades bars too
        notesAppendix,
        activities: active.map((a) => ({
          ...a,
          progress_pct: progressPct(a, subsByActivity.get(a.activity_id) ?? []),
          // mig 366 — resolve the same way the chart does, so the print and the
          // screen classify an unlabelled outage identically
          outage_impact: outageImpact(a),
        })),
        lanes: data?.lanes ?? [],
        clientVisibleOnly: audience === "client",
      });
    } catch (e: any) {
      alert(e?.message ?? "PDF export failed");
    }
  }

  /** Persist one activity edit, keep a linked milestone honest, refresh. */
  async function saveActivity(a: ProgramActivity, patch: Partial<ProgramActivity>) {
    const upd: any = { ...patch, updated_at: new Date().toISOString() };
    // keep start/days/end coherent whichever one moved
    const start = (patch.start_date !== undefined ? patch.start_date : a.start_date) ?? null;
    const days = (patch.days !== undefined ? patch.days : a.days) ?? null;
    if (start && days) upd.end_date = addDays(start, Math.max(days, 1) - 1);
    else if (patch.start_date !== undefined || patch.days !== undefined) upd.end_date = start;
    const { error } = await supabase.from("project_program_activities").update(upd).eq("activity_id", a.activity_id);
    if (error) { alert(`Couldn't save: ${error.message}`); return false; }
    if (a.milestone_id && (patch.start_date !== undefined || patch.name !== undefined || patch.client_visible !== undefined)) {
      await supabase.from("project_formal_milestones").update({
        ...(patch.name !== undefined ? { name: patch.name } : {}),
        ...(patch.start_date !== undefined ? { planned_date: patch.start_date } : {}),
        ...(patch.client_visible !== undefined ? { client_visible: patch.client_visible } : {}),
        updated_at: new Date().toISOString(),
      }).eq("milestone_id", a.milestone_id);
    }
    qc.invalidateQueries({ queryKey: ["program", parentKind, projectId] });
    return true;
  }

  // ----- bookings link (mig 343) -----
  const [moveConfirm, setMoveConfirm] = useState<{ a: ProgramActivity; booking: BookingRow; deltaDays: number } | null>(null);

  /** Drag handler: save the line, then — if it has a linked booking and the
   *  start moved — ASK whether the real booking follows. Never silent. */
  async function handleBarChange(a: ProgramActivity, startIso: string, days: number) {
    const oldStart = a.start_date;
    const ok = await saveActivity(a, { start_date: startIso, days });
    if (!ok) return;
    if (a.booking_id && oldStart && startIso !== oldStart) {
      const booking = (data?.bookings ?? []).find((b) => b.booking_id === a.booking_id);
      if (booking) {
        const deltaDays = Math.round((new Date(startIso + "T00:00:00").getTime() - new Date(oldStart + "T00:00:00").getTime()) / 864e5);
        if (deltaDays !== 0) setMoveConfirm({ a: { ...a, start_date: startIso, days }, booking, deltaDays });
      }
    }
  }

  async function moveLinkedBooking() {
    if (!moveConfirm) return;
    const { booking, deltaDays } = moveConfirm;
    const shift = (ts: string) => new Date(new Date(ts).getTime() + deltaDays * 864e5).toISOString();
    // 3 Nov 2026 — through the reschedule RPC so the move leaves a history row (status left alone: a program shift isn't a delay)
    try { await rescheduleProjectBooking(booking.booking_id, shift(booking.scheduled_start), booking.scheduled_end ? shift(booking.scheduled_end) : null, `Moved with the program line (${deltaDays > 0 ? "+" : ""}${deltaDays} days)`, false); }
    catch (e: any) { alert(`Couldn't move the booking: ${e?.message ?? e}`); }
    setMoveConfirm(null);
    qc.invalidateQueries({ queryKey: ["program", parentKind, projectId] });
  }

  async function linkBooking(a: ProgramActivity, bookingId: string | null) {
    const { error } = await supabase.from("project_program_activities")
      .update({ booking_id: bookingId, updated_at: new Date().toISOString() })
      .eq("activity_id", a.activity_id);
    if (error) { alert(`Couldn't ${bookingId ? "link" : "unlink"}: ${error.message}`); return; }
    qc.invalidateQueries({ queryKey: ["program", parentKind, projectId] });
  }

  async function createBookingFromLine(a: ProgramActivity) {
    if (!a.start_date) { alert("Give the line a start date first."); return; }
    const end = a.end_date ?? a.start_date;
    // 3 Nov 2026 — through the one helper: same row shape as before, plus the history row it never had.
    // An outage line still needs its CRQ before it's a booking — ask for it here rather than refuse silently.
    let crq: string | null = null;
    if (a.kind === "outage") { crq = window.prompt("CRQ number for this outage (required for the booking):", "")?.trim() || null; if (!crq) return; }
    let bookingId: string;
    try {
      bookingId = await createProjectBooking({ project_id: projectId, booking_type: a.kind === "outage" ? "outage" : "plant", title: a.name, day: a.start_date, end_day: end, show_on_schedule: true, crq_number: crq,
        notes: "Created from the build program — set the real times, contacts and approvals here." }, "Created from the build program");
    } catch (e: any) { alert(`Couldn't create the booking: ${e?.message ?? e}`); return; }
    await supabase.from("project_program_activities")
      .update({ booking_id: bookingId, updated_at: new Date().toISOString() })
      .eq("activity_id", a.activity_id);
    qc.invalidateQueries({ queryKey: ["program", parentKind, projectId] });
  }

  const subsByActivity = useMemo(() => {
    const m = new Map<string, SubRow[]>();
    for (const su of data?.subs ?? []) {
      if (!m.has(su.activity_id)) m.set(su.activity_id, []);
      m.get(su.activity_id)!.push(su);
    }
    return m;
  }, [data?.subs]);

  async function toggleSub(su: SubRow) {
    const { error } = await supabase.from("project_program_subactivities")
      .update({ done: !su.done }).eq("sub_id", su.sub_id);
    if (error) { alert(`Couldn't tick: ${error.message}`); return; }
    qc.invalidateQueries({ queryKey: ["program", parentKind, projectId] });
  }

  async function saveSubs(activityId: string, names: string[]) {
    const { error } = await supabase.schema("app").rpc("program_set_subs", {
      p_activity_id: activityId, p_names: names,
    });
    if (error) { alert(`Couldn't save sub-activities: ${error.message}`); return; }
    qc.invalidateQueries({ queryKey: ["program", parentKind, projectId] });
  }

  async function addActivity() {
    const firstLane = data?.lanes?.[0]?.lane_code ?? "CIVILS";
    const maxSort = active.reduce((m, a) => Math.max(m, a.sort_order), 0);
    const today = new Date();
    const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    const { data: row, error } = await supabase.from("project_program_activities").insert({
      [parentCol]: projectId,
      code: `NEW-${Date.now().toString(36).toUpperCase()}`,
      lane: firstLane, owner: "internal", name: "New activity", kind: "activity",
      start_date: todayIso, days: 1, end_date: todayIso, sort_order: maxSort + 1,
    }).select("*").single();
    if (error) { alert(`Couldn't add: ${error.message}`); return; }
    qc.invalidateQueries({ queryKey: ["program", parentKind, projectId] });
    setEditAct(row as ProgramActivity);
  }

  /** Put a line at a new position WITHIN ITS LANE.
   *
   *  James, 13 Sep: "How can I edit where a line sits in the rows? The SA outage
   *  3.4 I'd want to be 3.1 at the top." The old version swapped two rows'
   *  sort_order, which is fine for one step but wrong for a drag across five —
   *  so this permutes the lane's own set of sort_order values instead. The lane
   *  keeps exactly the numbers it already owned, just in a new sequence, which
   *  is why it can never collide with another lane's rows. */
  async function reorderInLane(a: ProgramActivity, toIndex: number) {
    const laneActs = active.filter((x) => x.lane === a.lane);
    const from = laneActs.findIndex((x) => x.activity_id === a.activity_id);
    if (from < 0) return;
    const to = Math.max(0, Math.min(laneActs.length - 1, toIndex));
    if (to === from) return;
    const next = [...laneActs];
    next.splice(to, 0, ...next.splice(from, 1));
    // the sort_order values this lane already holds, lowest first
    const slots = laneActs.map((x) => x.sort_order).sort((p, q) => p - q);
    const writes = next
      .map((x, i) => ({ id: x.activity_id, sort_order: slots[i], was: x.sort_order }))
      .filter((w) => w.sort_order !== w.was);
    for (const w of writes) {
      const { error } = await supabase.from("project_program_activities")
        .update({ sort_order: w.sort_order, updated_at: new Date().toISOString() })
        .eq("activity_id", w.id);
      if (error) { alert(`Couldn't reorder: ${error.message}`); break; }
    }
    qc.invalidateQueries({ queryKey: ["program", parentKind, projectId] });
  }
  const moveRow = (a: ProgramActivity, dir: -1 | 1) => {
    const laneActs = active.filter((x) => x.lane === a.lane);
    const i = laneActs.findIndex((x) => x.activity_id === a.activity_id);
    return reorderInLane(a, i + dir);
  };

  /** Renumber a lane's WBS to match the order on screen. Offered because moving
   *  a row does NOT touch its wbs — leaving "3.4" sitting above "3.1", which
   *  looks broken. The prefix is taken from the lane's first line, so a client's
   *  own numbering scheme is followed rather than invented. */
  async function renumberLane(lane: string) {
    const laneActs = active.filter((x) => x.lane === lane);
    if (!laneActs.length) return;
    const first = laneActs.find((x) => x.wbs && x.wbs.includes("."))?.wbs ?? laneActs[0].wbs ?? "1.1";
    const prefix = first.includes(".") ? first.slice(0, first.lastIndexOf(".") + 1) : "";
    if (!window.confirm(
      `Renumber the ${laneActs.length} lines in ${lane} as ${prefix}1 … ${prefix}${laneActs.length}, in the order they're shown?\n\n` +
      `Only the WBS numbers change. If the client issued these numbers, don't.`,
    )) return;
    for (let i = 0; i < laneActs.length; i++) {
      const wbs = `${prefix}${i + 1}`;
      if (laneActs[i].wbs === wbs) continue;
      await supabase.from("project_program_activities")
        .update({ wbs, updated_at: new Date().toISOString() })
        .eq("activity_id", laneActs[i].activity_id);
    }
    qc.invalidateQueries({ queryKey: ["program", parentKind, projectId] });
  }

  if (isLoading) return <div className="py-12 flex justify-center"><Spinner size={22} /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <CalendarRange size={18} className="text-accent" />
          <h2 className="text-[15px] font-semibold">Build program</h2>
          {latestRev && (
            <Pill small label={`Rev ${latestRev.revision_no}${latestRev.revision_label ? ` · ${latestRev.revision_label}` : ""}`} />
          )}
          {latestRev && (
            <select
              value={ISSUE_STATUSES.includes(issueStatus as any) ? issueStatus : "Draft"}
              onChange={(e) => void setIssueStatus(e.target.value)}
              title="Printed as the Status on the PDF and the Excel. Yours to set — a programme stops being a draft when you say so."
              className={`text-[11px] rounded-full border px-2 py-0.5 cursor-pointer bg-transparent ${
                issueStatus === "Issued for construction" ? "border-ok/50 text-ok"
                : issueStatus === "Superseded" ? "border-line text-text-dim line-through"
                : issueStatus === "For review" ? "border-warn/50 text-warn"
                : "border-line text-text-muted"}`}
            >
              {ISSUE_STATUSES.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          )}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {active.length > 0 && (
            <>
              <div className="flex rounded-lg border border-line overflow-hidden mr-1">
                {(["gantt", "table"] as const).map((v) => (
                  <button
                    key={v}
                    onClick={() => setView(v)}
                    className={`px-2.5 py-1 text-[12px] inline-flex items-center gap-1 ${view === v ? "bg-accent/10 text-accent font-medium" : "text-text-muted hover:text-text"}`}
                  >
                    {v === "gantt" ? <CalendarRange size={12} /> : <List size={12} />}
                    {v === "gantt" ? "Gantt" : "Table"}
                  </button>
                ))}
              </div>
              <div className="flex rounded-lg border border-line overflow-hidden mr-1"
                title="Client view hides every line marked 'client can't see this' — from the chart AND from the PDF and Excel. Preview it here before you send it.">
                {(["internal", "client"] as const).map((v) => (
                  <button
                    key={v}
                    onClick={() => setAudience(v)}
                    className={`px-2.5 py-1 text-[12px] inline-flex items-center gap-1 ${audience === v ? (v === "client" ? "bg-ok/10 text-ok font-medium" : "bg-accent/10 text-accent font-medium") : "text-text-muted hover:text-text"}`}
                  >
                    {v === "internal" ? <Eye size={12} /> : <Send size={12} />}
                    {v === "internal" ? "Internal" : "Client view"}
                  </button>
                ))}
              </div>
              {keyCount > 0 && audience === "internal" && (
                <Button variant="ghost" size="sm" onClick={() => void clearKeyDates()}
                  title="Ops used to star every milestone on import. Clear them all and flag only the ones you want.">
                  Clear {keyCount} key date{keyCount === 1 ? "" : "s"}
                </Button>
              )}
              <Button variant="ghost" size="sm" icon={Plus} onClick={() => void addActivity()} disabled={audience === "client"}>Add line</Button>
              {/* James, 13 Sep: "lets have a note readability toggle like we will
                  have the build plan, so we can choose what kind of PDF we
                  generate to send the client." One Export door, the choices
                  inside it, instead of a toolbar that grows a button per format. */}
              <Button variant="ghost" size="sm" icon={FileText} onClick={() => setExportOpen(true)}>Export</Button>
              <Button variant="ghost" size="sm" icon={copied ? Check : Copy} onClick={copyJson}>
                {copied ? "Copied" : "Copy JSON"}
              </Button>
            </>
          )}
          <Button size="sm" icon={Upload} onClick={() => setImportOpen(true)}>Import program</Button>
        </div>
      </div>

      {/* mig 369 — say plainly whose program this is and what happens when the
          bid is won, because the answer ("it comes with it") is the reason to
          build it here at all. */}
      {isBid && (
        <div className="border border-info/40 bg-info/5 rounded-lg px-3 py-2 text-[12.5px] flex items-start gap-2">
          <HardHat size={13} className="text-info mt-0.5 shrink-0" />
          <span className="text-text-muted">
            <span className="font-medium text-info">Planning a job we haven't won.</span>{" "}
            This program belongs to the bid — it stays off the crew calendar, the field build plan
            and the display board. Win the bid and the whole thing moves onto the new project,
            dates, sub-tasks, ticks and key dates intact.
          </span>
        </div>
      )}

      {audience === "client" && (
        <div className="border border-ok/40 bg-ok/5 rounded-lg px-3 py-2 text-[12.5px] flex items-center gap-2 flex-wrap">
          <Send size={13} className="text-ok" />
          <span className="font-medium text-ok">Client view</span>
          <span className="text-text-muted">
            {hiddenFromClient > 0
              ? `${hiddenFromClient} internal ${hiddenFromClient === 1 ? "line is" : "lines are"} hidden — this is exactly what the PDF and Excel will contain.`
              : "Every line is client-visible — the PDF and Excel will show all of them."}
          </span>
          <button className="ml-auto text-text-muted hover:text-text underline decoration-dotted underline-offset-2"
            onClick={() => setAudience("internal")}>Back to internal</button>
        </div>
      )}

      {active.length === 0 ? (
        <Card className="p-8 text-center">
          <CalendarRange size={28} className="mx-auto mb-3 text-text-muted" />
          <div className="text-[14px] font-medium mb-1">No build program yet</div>
          <div className="text-[13px] text-text-muted max-w-md mx-auto mb-4">
            Paste the program JSON from your Projects Claude (Build Program directive), or upload a
            Coretel Gantt workbook — every change shows a review before anything applies.
          </div>
          <Button size="sm" icon={Upload} onClick={() => setImportOpen(true)}>Import program</Button>
        </Card>
      ) : view === "gantt" ? (
        <div style={{ width: "calc(100vw - 48px)", marginLeft: "calc(50% - 50vw + 24px)" }}>
          <GanttView
            grouped={grouped}
            onReorder={(a, to) => void reorderInLane(a, to)}
            onRenumberLane={(lane) => void renumberLane(lane)}
            canReorder={audience === "internal"}
            laneColor={laneColor}
            bookings={data?.bookings ?? []}
            crewWindows={data?.crewWindows ?? []}
            subsByActivity={subsByActivity}
            onToggleSub={(su) => void toggleSub(su)}
            onBarChange={(a, startIso, days) => void handleBarChange(a, startIso, days)}
            onTogglePlan={(a) => void saveActivity(a, { in_build_plan: !a.in_build_plan } as Partial<ProgramActivity>)}
            onToggleDone={(a) => void saveActivity(a, { done: !a.done } as Partial<ProgramActivity>)}
            onBarClick={(a) => setEditAct(a)}
          />
        </div>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-text-muted border-b border-line">
                  <th className="px-3 py-2 font-medium">WBS</th>
                  <th className="px-3 py-2 font-medium">Activity</th>
                  <th className="px-3 py-2 font-medium">Who</th>
                  <th className="px-3 py-2 font-medium">Resource</th>
                  <th className="px-3 py-2 font-medium">Start</th>
                  <th className="px-3 py-2 font-medium text-right">Days</th>
                  <th className="px-3 py-2 font-medium">Finish</th>
                  <th className="px-2 py-2 font-medium text-center" title="On the crew's field build plan (mig 348)">Field</th>
                  <th className="px-1 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {grouped.map(([lane, acts]) => (
                  <React.Fragment key={lane}>
                    <tr>
                      <td colSpan={9} className="px-3 pt-3 pb-1">
                        <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
                          <span className="w-2.5 h-2.5 rounded-sm" style={{ background: laneColor(lane) }} />
                          {lane}
                        </span>
                      </td>
                    </tr>
                    {acts.map((a) => {
                      const external = a.owner !== "internal";
                      return (
                        <tr key={a.activity_id} className="border-b border-line/50 hover:bg-ink-raised/30">
                          <td className="px-3 py-1.5 text-text-muted whitespace-nowrap">{a.wbs ?? ""}</td>
                          <td className="px-3 py-1.5 cursor-pointer" onClick={() => setEditAct(a)} title="Click to edit this line">
                            <span className="inline-flex items-center gap-1.5">
                              {a.kind === "milestone" || a.kind === "client_deliverable" ? (
                                <Diamond size={11} className="text-accent shrink-0" fill="currentColor" />
                              ) : a.kind === "hold" || a.kind === "outage" ? (
                                <span className="w-2 h-2 rounded-full border border-dashed border-text-muted shrink-0" />
                              ) : null}
                              <span className={external ? "text-text-muted" : ""}>{a.name}</span>
                            </span>
                            {a.notes && <div className="text-[11.5px] text-text-muted pl-0.5">{a.notes}</div>}
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            <OwnerChip activity={a} projectId={projectId} parentKind={parentKind} />
                          </td>
                          <td className="px-3 py-1.5 text-text-muted whitespace-nowrap">{a.resource ?? ""}</td>
                          <td className="px-3 py-1.5 whitespace-nowrap">{fmtDate(a.start_date)}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{a.days ?? ""}</td>
                          <td className="px-3 py-1.5 whitespace-nowrap">{fmtDate(a.end_date)}</td>
                          <td className="px-2 py-1.5 text-center">
                            {/* mig 348 — one click on/off the crew's build plan, no modal */}
                            <input
                              type="checkbox"
                              checked={a.in_build_plan}
                              onChange={() => void saveActivity(a, { in_build_plan: !a.in_build_plan } as Partial<ProgramActivity>)}
                              className="cursor-pointer accent-[#e2661a]"
                              title={a.in_build_plan ? "On the field build plan — click to take it off" : "Not on the field build plan — click to put it on"}
                            />
                          </td>
                          <td className="px-1 py-1.5 whitespace-nowrap">
                            <button onClick={() => void moveRow(a, -1)} className="text-text-muted hover:text-text p-0.5" title="Move up"><ArrowUp size={12} /></button>
                            <button onClick={() => void moveRow(a, 1)} className="text-text-muted hover:text-text p-0.5" title="Move down"><ArrowDown size={12} /></button>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-3 py-2 text-[11.5px] text-text-muted border-t border-line">
            {active.length} activities · client &amp; subbie lines shown muted — we work around those dates.
            "Field" ticks a line onto the crew's build plan (Coretel lines are on by default).
          </div>
        </Card>
      )}

      {/* Export chooser — one door for every artefact the program can produce,
          so the toolbar doesn't grow a button per format and each format can
          carry its own options. */}
      {exportOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center pt-20 px-4 overflow-y-auto"
          onClick={() => setExportOpen(false)}>
          <div className="bg-ink-card rounded-xl border border-line w-full max-w-lg mb-10" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-line">
              <div className="text-[14px] font-semibold">Export program</div>
              <button onClick={() => setExportOpen(false)} className="text-text-muted hover:text-text"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className={`text-[12px] rounded-lg px-3 py-2 ${audience === "client"
                ? "bg-info/10 text-info" : "bg-warn/10 text-warn"}`}>
                {audience === "client"
                  ? `Client view — ${activeAll.length - active.length} internal-only line${activeAll.length - active.length === 1 ? "" : "s"} will be left out.`
                  : "Internal view — every line goes out, including internal-only ones. Switch to Client view above before sending this to a client."}
              </div>

              <div className="border border-line rounded-lg p-3.5">
                <div className="flex items-start gap-2.5">
                  <FileText size={16} className="mt-0.5 text-accent shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold">Chart PDF</div>
                    <div className="text-[11px] text-text-muted">The Gantt on one page — what the client usually gets.</div>
                    <label className="flex items-start gap-2 mt-2.5 text-[12px] cursor-pointer">
                      <input type="checkbox" className="mt-0.5" checked={notesAppendix}
                        onChange={(e) => setNotesAppendix(e.target.checked)} />
                      <span>
                        Add a notes page
                        <span className="block text-[11px] text-text-muted">
                          Chart rows clip notes at about 34 characters. This adds a page after the chart with every note in full, grouped by lane.
                        </span>
                      </span>
                    </label>
                  </div>
                </div>
                <div className="mt-3 flex justify-end">
                  <Button size="sm" icon={FileText}
                    onClick={() => { setExportOpen(false); void downloadPdf(); }}>Download PDF</Button>
                </div>
              </div>

              <div className="border border-line rounded-lg p-3.5">
                <div className="flex items-start gap-2.5">
                  <FileSpreadsheet size={16} className="mt-0.5 text-ok shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold">Gantt workbook (Excel)</div>
                    <div className="text-[11px] text-text-muted">Setup / Schedule / Summary sheets with painted bars — for anyone who wants to work the dates in Excel.</div>
                  </div>
                </div>
                <div className="mt-3 flex justify-end">
                  <Button variant="ghost" size="sm" icon={FileSpreadsheet}
                    onClick={() => { setExportOpen(false); void downloadExcel(); }}>Download Excel</Button>
                </div>
              </div>

              {/* Weekly build plan. James, 13 Sep: don't put the head
                  contractor's name on it — both are our clients and one doesn't
                  need to see the other's on a document we send. */}
              <div className="border border-line rounded-lg p-3.5">
                <div className="flex items-start gap-2.5">
                  <HardHat size={16} className="mt-0.5 text-info shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold">Weekly build plan (Excel)</div>
                    <div className="text-[11px] text-text-muted">
                      The same program as a week-by-week calendar — each day's sub-activities written
                      into the Riggers and Technician rows. A step tagged <span className="font-mono">R</span> / <span className="font-mono">T</span> / <span className="font-mono">C</span> on
                      the line goes to its own row (civil sits with the riggers); a line whose steps are
                      split heads both rows. The lane never decides. Long outage times, overnight carriers
                      and comments are left blank for the client's integration lead.
                    </div>
                    <div className="mt-2.5">
                      <div className="text-[11px] uppercase tracking-wide text-text-muted mb-1">Untagged work — lines with no steps, steps with no R / T / C</div>
                      <div className="text-[11px] text-text-muted mb-1.5">It's everyone's unless you say otherwise. Tag the steps on the line to split a mixed day.</div>
                      <div className="flex rounded-md border border-line overflow-hidden w-fit">
                        {(["both", "external", "internal"] as const).map((v) => (
                          <button key={v} type="button"
                            onClick={() => setPlanUntagged(v)}
                            className={`px-2.5 py-1 text-[11px] ${planUntagged === v ? "bg-accent text-white" : "text-text-muted hover:text-text"}`}>
                            {v === "both" ? "Both rows" : v === "external" ? "Riggers only" : "Technician only"}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex justify-end">
                  <Button variant="ghost" size="sm" icon={HardHat}
                    onClick={() => { setExportOpen(false); void downloadBuildPlan(); }}>Download build plan</Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {importOpen && (
        <ImportModal
          projectId={projectId}
          projectCode={projectCode}
          isBid={isBid}
          current={data?.activities ?? []}
          subsByActivity={subsByActivity}
          onClose={() => setImportOpen(false)}
          onApplied={() => { setImportOpen(false); qc.invalidateQueries({ queryKey: ["program", parentKind, projectId] }); }}
        />
      )}

      {editAct && editAct !== "new" && (
        <EditActivityModal
          activity={editAct}
          lanes={data?.lanes ?? []}
          bookings={data?.bookings ?? []}
          isBid={isBid}
          subs={subsByActivity.get(editAct.activity_id) ?? []}
          onSaveSubs={(names) => void saveSubs(editAct.activity_id, names)}
          onLinkBooking={(id) => void linkBooking(editAct, id)}
          onCreateBooking={() => void createBookingFromLine(editAct)}
          onClose={() => setEditAct(null)}
          onSave={async (patch) => { const ok = await saveActivity(editAct, patch); if (ok) setEditAct(null); }}
          onDelete={async () => {
            const ok = await saveActivity(editAct, { is_active: false } as any);
            if (ok) setEditAct(null);
          }}
        />
      )}

      {moveConfirm && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-4" onClick={() => setMoveConfirm(null)}>
          <div className="bg-ink-card rounded-xl border border-line w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-2">
              <Link2 size={15} className="text-accent" />
              <div className="text-[14px] font-semibold">Move the linked booking too?</div>
            </div>
            <div className="text-[13px] text-text-muted mb-3">
              <span className="text-text font-medium">{moveConfirm.a.name}</span> moved{" "}
              {Math.abs(moveConfirm.deltaDays)} day{Math.abs(moveConfirm.deltaDays) === 1 ? "" : "s"}{" "}
              {moveConfirm.deltaDays > 0 ? "later" : "earlier"}. Its booking{" "}
              <span className="text-text font-medium">{moveConfirm.booking.title}</span>{" "}
              ({moveConfirm.booking.booking_type} · {moveConfirm.booking.status}
              {moveConfirm.booking.booking_type === "outage" && moveConfirm.booking.outage_approved ? " · APPROVED" : ""})
              currently starts {new Date(moveConfirm.booking.scheduled_start).toLocaleString("en-AU", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}.
              Moving keeps its times of day{moveConfirm.booking.booking_type === "outage" ? " — an approved outage may need the client told" : ""}.
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setMoveConfirm(null)}>Leave the booking</Button>
              <Button size="sm" icon={Check} onClick={() => void moveLinkedBooking()}>
                Move it {moveConfirm.deltaDays > 0 ? "+" : ""}{moveConfirm.deltaDays}d
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// GanttView — the bars. Drag the middle to move (days stay put), drag either
// end to grow/shrink, click to edit — the same muscle memory as the crew
// schedule. Client/subbie bars render dashed: their dates, we work around them.
// ---------------------------------------------------------------------------
const DAY_W = 26;
const ROW_H = 34;
const LABEL_W = 400;

function isoAdd(iso: string, n: number): string { return addDays(iso, n); }
function isoOf(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function GanttView({ grouped, laneColor, bookings, crewWindows, subsByActivity, onToggleSub, onBarChange, onTogglePlan, onToggleDone, onBarClick, onReorder, onRenumberLane, canReorder }: {
  grouped: [string, ProgramActivity[]][];
  laneColor: (lane: string | null) => string;
  bookings: BookingRow[];
  crewWindows: { assigned_from: string; assigned_to: string | null }[];
  subsByActivity: Map<string, SubRow[]>;
  onToggleSub: (su: SubRow) => void;
  onBarChange: (a: ProgramActivity, startIso: string, days: number) => void;
  onTogglePlan: (a: ProgramActivity) => void;
  onToggleDone: (a: ProgramActivity) => void;
  onBarClick: (a: ProgramActivity) => void;
  onReorder: (a: ProgramActivity, toIndex: number) => void;
  onRenumberLane: (lane: string) => void;
  /** Client view hides internal-only lines, so the row indexes on screen are not
   *  the lane's real membership — reordering from a partial list would move rows
   *  the user cannot see. Same reason Add line is disabled there. */
  canReorder: boolean;
}) {
  // which lines have their sub-activities open (hidden by default — James:
  // "nest under the line / hide when not wanted")
  const [openSubs, setOpenSubs] = useState<Set<string>>(new Set());
  const toggleOpen = (id: string) => setOpenSubs((prev) => {
    const nx = new Set(prev);
    nx.has(id) ? nx.delete(id) : nx.add(id);
    return nx;
  });
  const bookingById = useMemo(() => new Map(bookings.map((b) => [b.booking_id, b])), [bookings]);
  const hasCrewOver = (a: ProgramActivity): boolean => {
    if (!a.start_date) return true; // nothing to cover yet
    const end = a.end_date ?? a.start_date;
    return crewWindows.some((w) => w.assigned_from <= end && (w.assigned_to ?? "9999-12-31") >= a.start_date!);
  };
  /** Is there a plant booking whose window touches this line's dates? */
  const plantCovered = (a: ProgramActivity): boolean => {
    if (!a.start_date) return false;
    const end = a.end_date ?? a.start_date;
    return bookings.some((b) => {
      if (b.booking_type !== "plant") return false;
      const bs = tsDay(b.scheduled_start);
      const be = b.scheduled_end ? tsDay(b.scheduled_end) : bs;
      return bs <= end && be >= a.start_date!;
    });
  };
  const all = grouped.flatMap(([, acts]) => acts);
  const dated = all.filter((a) => a.start_date);
  // window: Monday before first start → Sunday after last end, min 3 weeks
  const winStart = useMemo(() => {
    const base = dated.length
      ? dated.reduce((m, a) => (a.start_date! < m ? a.start_date! : m), dated[0].start_date!)
      : isoOf(new Date());
    const d = new Date(base + "T00:00:00");
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7) - 7); // Monday, one spare week
    return isoOf(d);
  }, [all.length, dated.map((a) => a.start_date).join(",")]); // eslint-disable-line react-hooks/exhaustive-deps
  const totalDays = useMemo(() => {
    const maxEnd = dated.length
      ? dated.reduce((m, a) => ((a.end_date ?? a.start_date!) > m ? (a.end_date ?? a.start_date!) : m), dated[0].end_date ?? dated[0].start_date!)
      : winStart;
    const n = Math.round((new Date(maxEnd + "T00:00:00").getTime() - new Date(winStart + "T00:00:00").getTime()) / 864e5) + 14;
    return Math.max(n, 28);
  }, [winStart, dated.map((a) => a.end_date).join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  const idxOf = (iso: string) => Math.round((new Date(iso + "T00:00:00").getTime() - new Date(winStart + "T00:00:00").getTime()) / 864e5);
  const todayIdx = idxOf(isoOf(new Date()));

  // drag state — one bar at a time; live preview via offset in days
  const dragRef = useRef<{ a: ProgramActivity; mode: "move" | "l" | "r"; startX: number } | null>(null);
  const [dragPreview, setDragPreview] = useState<{ id: string; startIdx: number; days: number } | null>(null);
  /** Vertical row drag (James, 13 Sep: "drag drop would be amazing").
   *  Deliberately separate from the horizontal bar drag above: that one lives on
   *  the BAR and changes dates, this one lives on the GRIP in the label cell and
   *  changes order. Two handles, two meanings, no chance of doing one while
   *  meaning the other. `over` is the index the line would land at. */
  const [rowDrag, setRowDrag] = useState<{ id: string; lane: string; from: number; over: number } | null>(null);

  function beginRowDrag(e: React.PointerEvent, a: ProgramActivity, lane: string, from: number, count: number) {
    e.preventDefault();
    e.stopPropagation();
    const startY = e.clientY;
    setRowDrag({ id: a.activity_id, lane, from, over: from });
    const onMove = (ev: PointerEvent) => {
      const delta = Math.round((ev.clientY - startY) / ROW_H);
      const over = Math.max(0, Math.min(count - 1, from + delta));
      setRowDrag((cur) => (cur && cur.over !== over ? { ...cur, over } : cur));
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      setRowDrag((cur) => {
        if (cur && cur.over !== cur.from) onReorder(a, cur.over);
        return null;
      });
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }

  function beginDrag(e: React.MouseEvent, a: ProgramActivity, mode: "move" | "l" | "r") {
    if (!a.start_date) return;
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = { a, mode, startX: e.clientX };
    const startIdx = idxOf(a.start_date);
    const days = Math.max(a.days ?? 1, 1);
    setDragPreview({ id: a.activity_id, startIdx, days });

    const onMove = (ev: MouseEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const delta = Math.round((ev.clientX - d.startX) / DAY_W);
      const s0 = idxOf(d.a.start_date!);
      const n0 = Math.max(d.a.days ?? 1, 1);
      if (d.mode === "move") setDragPreview({ id: d.a.activity_id, startIdx: s0 + delta, days: n0 });
      else if (d.mode === "l") {
        const ns = Math.min(s0 + delta, s0 + n0 - 1);
        setDragPreview({ id: d.a.activity_id, startIdx: ns, days: n0 - (ns - s0) });
      } else {
        setDragPreview({ id: d.a.activity_id, startIdx: s0, days: Math.max(n0 + delta, 1) });
      }
    };
    const onUp = (ev: MouseEvent) => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      const d = dragRef.current;
      dragRef.current = null;
      setDragPreview((prev) => {
        if (d && prev && prev.id === d.a.activity_id) {
          const moved = Math.abs(ev.clientX - d.startX) >= DAY_W / 2;
          const s0 = idxOf(d.a.start_date!);
          const n0 = Math.max(d.a.days ?? 1, 1);
          if (moved && (prev.startIdx !== s0 || prev.days !== n0)) {
            onBarChange(d.a, isoAdd(winStart, prev.startIdx), prev.days);
          } else if (!moved) {
            onBarClick(d.a);
          }
        }
        return null;
      });
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }

  // open the chart around TODAY, not the start of history
  const scrollRef = useRef<HTMLDivElement>(null);
  const didAutoScroll = useRef(false);
  React.useEffect(() => {
    if (didAutoScroll.current || !scrollRef.current) return;
    didAutoScroll.current = true;
    scrollRef.current.scrollLeft = Math.max(0, (todayIdx - 4) * DAY_W);
  }, [todayIdx]);

  // header cells
  const days: { idx: number; d: Date }[] = [];
  for (let i = 0; i < totalDays; i++) {
    days.push({ idx: i, d: new Date(new Date(winStart + "T00:00:00").getTime() + i * 864e5) });
  }
  const months: { label: string; startIdx: number; span: number }[] = [];
  for (const { idx, d } of days) {
    const label = d.toLocaleDateString("en-AU", { month: "short", year: "2-digit" });
    const last = months[months.length - 1];
    if (!last || last.label !== label) months.push({ label, startIdx: idx, span: 1 });
    else last.span++;
  }

  return (
    <Card className="overflow-hidden">
      {/* one scroll container for both axes: the horizontal scrollbar lives at
          the bottom of THIS box (max ~70% of the viewport), not the page —
          James: "the gantt scroll bar should follow the page". Header sticks
          to the top of it, task names stick to the left of it. */}
      {/* height hugs the viewport so the horizontal scrollbar is on screen
          without scrolling the page to the bottom (James, 3 Sep) */}
      <div ref={scrollRef} className="overflow-auto overscroll-x-contain" style={{ maxHeight: "max(420px, calc(100vh - 310px))" }}>
        <div style={{ width: LABEL_W + totalDays * DAY_W }} className="relative select-none">
          {/* month + day header */}
          <div className="flex sticky top-0 z-20 bg-ink-card">
            <div style={{ width: LABEL_W }} className="shrink-0 sticky left-0 z-30 bg-ink-card" />
            <div className="relative" style={{ width: totalDays * DAY_W, height: 40 }}>
              {months.map((m) => (
                <div key={m.label + m.startIdx} className="absolute top-0 text-[10.5px] font-semibold text-text-muted uppercase tracking-wide pl-1"
                  style={{ left: m.startIdx * DAY_W, width: m.span * DAY_W }}>
                  {m.label}
                </div>
              ))}
              {days.map(({ idx, d }) => {
                const we = d.getDay() === 0 || d.getDay() === 6;
                // while dragging, the landing window lights up in the header —
                // same as the crew schedule (James, 3 Sep)
                const lit = dragPreview != null && idx >= dragPreview.startIdx && idx <= dragPreview.startIdx + dragPreview.days - 1;
                const litEnd = lit && (idx === dragPreview!.startIdx || idx === dragPreview!.startIdx + dragPreview!.days - 1);
                return (
                  <div key={idx} className={`absolute bottom-0 text-center text-[10px] ${lit ? "text-text font-semibold" : idx === todayIdx ? "text-accent font-bold" : we ? "text-text-muted/50" : "text-text-muted"}`}
                    style={{ left: idx * DAY_W, width: DAY_W,
                      background: lit ? "color-mix(in srgb, var(--accent, #e2661a) 38%, transparent)" : undefined,
                      borderRadius: litEnd ? 4 : 0,
                      boxShadow: litEnd ? "inset 0 -2px 0 var(--accent, #e2661a)" : undefined }}>
                    {d.getDate()}
                  </div>
                );
              })}
              {todayIdx >= 0 && todayIdx < totalDays && (
                <div className="absolute -top-0.5 text-[8px] font-bold uppercase tracking-wide text-white bg-accent rounded px-1 py-px pointer-events-none"
                  style={{ left: todayIdx * DAY_W + DAY_W / 2, transform: "translateX(-50%)" }}>
                  Today
                </div>
              )}
            </div>
          </div>

          {/* lanes + rows */}
          {grouped.map(([lane, acts]) => (
            <React.Fragment key={lane}>
              <div className="flex items-center border-t border-line bg-ink-raised/20" style={{ height: 26 }}>
                <div style={{ width: LABEL_W }} className="shrink-0 px-3 flex items-center gap-2 sticky left-0 z-10">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ background: laneColor(lane) }} />
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">{lane}</span>
                  {/* moving a row doesn't touch its WBS, so "3.4" can end up above
                      "3.1". This puts the numbers back in step, on demand. */}
                  {canReorder && (
                  <button
                    onClick={() => onRenumberLane(lane)}
                    className="ml-auto mr-2 text-[10px] px-1.5 py-px rounded border border-line text-text-dim hover:text-text hover:border-text-muted"
                    title={`Renumber this lane's WBS to match the order shown. Don't, if the client issued these numbers.`}
                  >
                    Renumber
                  </button>
                  )}
                </div>
              </div>
              {acts.map((a, rowIdx) => {
                const external = a.owner !== "internal";
                const preview = dragPreview?.id === a.activity_id ? dragPreview : null;
                const sIdx = preview ? preview.startIdx : a.start_date ? idxOf(a.start_date) : null;
                const nDays = preview ? preview.days : Math.max(a.days ?? 1, 1);
                const isMilestone = a.kind === "milestone" || a.kind === "client_deliverable";
                const isPlant = a.kind === "plant";
                const isOutage = a.kind === "outage";
                const isWindow = a.kind === "hold" || a.kind === "outage";
                // James, 13 Sep (Atherton): "Contingency / buffer is currently a
                // hold, which renders like a client bar. A buffer is OURS, and it
                // is deliberately empty — it should look deliberately empty."
                // He is right: the bar chain had `external || isWindow` sharing one
                // style, so our own buffer was indistinguishable from someone
                // else's work. A hold now gets a neutral hatch — blocked-out time,
                // nobody's work — while dashed-lane-colour keeps meaning "theirs".
                const isHold = a.kind === "hold";
                // mig 371
                const isAcceptance = a.kind === "acceptance";
                const isTravel = a.kind === "travel";
                const isStanddown = a.kind === "standdown";
                // James, 13 Sep: "NSA CRQs are standard and don't need to be
                // called out like this in every Gantt." An NSA is a paperwork
                // cover over a build window; an SA takes customers off air.
                const impact = outageImpact(a);
                const isSA = impact === "sa";
                const isNSA = impact === "nsa";
                const isNight = a.night_shift;
                const underOutage = a.under_outage;
                // James, 3 Sep: "highlight plant and outages or key milestone
                // dates like pour day — important tasks should not hide in the
                // mass of information." Milestones/plant/outages get a tinted
                // row, an icon on the name, and their own bar treatment.
                // James, 3 Sep: importance is HIS call — so the Key-date star is
                // what says "look at this", not the line's kind ("why HOP?
                // MS5020 is far more important").
                //
                // 13 Sep: the three washes were all ~5% alpha, which on the card
                // background read as the same faint orange — "even when I untick
                // it, an outage is still highlighted like a key date". So they're
                // now told apart by FORM, not just hue:
                //   · the WASH says what KIND of line it is (red = outage,
                //     gold = plant) and is strong enough to actually read;
                //   · the SPINE down the left says JAMES flagged it (is_key).
                // A line can be both — an outage he starred keeps the red wash
                // and gains the spine. Unticking Key date now visibly removes
                // something, which it didn't before.
                // Only a SERVICE-AFFECTING outage earns a full-row wash. An NSA
                // gets its blue chip and nothing else — it's routine.
                const rowTint = isSA ? "rgba(208,82,82,.11)"
                  : isPlant ? "rgba(201,162,39,.10)"
                  : a.is_key ? "rgba(226,102,26,.05)"
                  : undefined;
                const keySpine = a.is_key ? "inset 3px 0 0 rgba(226,102,26,.9)" : undefined;
                const subsHere = subsByActivity.get(a.activity_id) ?? [];
                // mig 358 — derived progress for the shading + badge (null = line doesn't track progress)
                const pct = progressPct(a, subsHere);
                return (
                  <React.Fragment key={a.activity_id}>
                  <div className={`group/row flex border-t border-line/40 hover:bg-ink-raised/20 ${
                      rowDrag?.id === a.activity_id ? "opacity-40" : ""}`}
                    style={{
                      height: ROW_H,
                      background: rowTint,
                      boxShadow: rowDrag && rowDrag.lane === lane && rowDrag.id !== a.activity_id && rowDrag.over === rowIdx
                        ? (rowDrag.over > rowDrag.from
                            ? "inset 0 -2px 0 var(--accent, #e2661a)"
                            : "inset 0 2px 0 var(--accent, #e2661a)")
                        : undefined,
                    }}>
                    {/* The label cell is sticky and opaque so it doesn't smear
                        while the chart scrolls — which is why the key-date spine
                        lives here rather than on the row. */}
                    <div style={{ width: LABEL_W, boxShadow: keySpine }} className="shrink-0 px-3 flex items-center gap-1.5 cursor-pointer overflow-hidden sticky left-0 z-10 bg-ink-card border-r border-line/40"
                      onClick={() => onBarClick(a)} title={`${a.name}${a.notes ? ` — ${a.notes}` : ""}${a.is_key ? " · KEY DATE" : ""} (click to edit)`}>
                      {(subsByActivity.get(a.activity_id)?.length ?? 0) > 0 ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleOpen(a.activity_id); }}
                          className="shrink-0 text-text-muted hover:text-text -ml-1"
                          title="Show / hide sub-activities"
                        >
                          {openSubs.has(a.activity_id) ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                        </button>
                      ) : (
                        <span className="w-[12px] shrink-0" />
                      )}
                      {/* Grip = reorder. Dragging the BAR moves dates; dragging
                          THIS moves the row. Arrows are the precise fallback and
                          the only way that works on a phone. */}
                      {canReorder && (
                      <span className="shrink-0 flex items-center -ml-0.5 mr-0.5"
                        onClick={(e) => e.stopPropagation()}>
                        <span
                          onPointerDown={(e) => beginRowDrag(e, a, lane, rowIdx, acts.length)}
                          className="cursor-grab active:cursor-grabbing text-text-dim/50 hover:text-text-muted px-0.5"
                          title="Drag to move this line up or down its lane"
                          aria-label="Drag to reorder"
                        >
                          <GripVertical size={11} />
                        </span>
                        <span className="hidden group-hover/row:flex flex-col leading-none">
                          <button onClick={() => onReorder(a, rowIdx - 1)} disabled={rowIdx === 0}
                            className="text-text-dim/60 hover:text-text disabled:opacity-25 disabled:cursor-default"
                            title="Move up"><ArrowUp size={9} /></button>
                          <button onClick={() => onReorder(a, rowIdx + 1)} disabled={rowIdx === acts.length - 1}
                            className="text-text-dim/60 hover:text-text disabled:opacity-25 disabled:cursor-default"
                            title="Move down"><ArrowDown size={9} /></button>
                        </span>
                      </span>
                      )}
                      <span className="text-[11px] text-text-muted w-8 shrink-0">{a.wbs ?? ""}</span>
                      {/* Marks, not washes. The row tints measure ΔE 1.3–3.2 apart
                          on the card — below the ~15 needed to tell two colours
                          apart with full colour vision, let alone without — so the
                          tint is a scanning aid and THESE carry the meaning. Each
                          is a filled chip in a semantic theme colour, so it survives
                          dark mode and a colour-blind reader alike. */}
                      {isMilestone && (
                        <span className="shrink-0 inline-flex items-center justify-center w-[15px] h-[15px] rounded-full bg-accent/15" title="Milestone">
                          <Diamond size={9} className="text-accent" fill="currentColor" />
                        </span>
                      )}
                      {isPlant && (
                        <span className="shrink-0 inline-flex items-center justify-center w-[15px] h-[15px] rounded-full bg-warn/15" title="Plant on site">
                          <Truck size={9.5} className="text-warn" />
                        </span>
                      )}
                      {isSA && (
                        <span className="shrink-0 inline-flex items-center justify-center w-[15px] h-[15px] rounded-full bg-danger/15" title="SERVICE AFFECTING outage — customers off air">
                          <Zap size={9.5} className="text-danger" fill="currentColor" />
                        </span>
                      )}
                      {isNSA && (
                        <span className="shrink-0 inline-flex items-center justify-center w-[15px] h-[15px] rounded-full bg-info/15" title="Non service affecting outage — routine cover, no customer impact">
                          <Zap size={9.5} className="text-info" />
                        </span>
                      )}
                      {underOutage && (
                        <span className="shrink-0 inline-flex items-center justify-center w-[15px] h-[15px] rounded-full" style={{ background: "rgba(18,128,92,.16)" }}
                          title="OUTAGE ACTIVITY — performed inside a service-affecting window; the network is down while this happens">
                          <AlertTriangle size={9.5} style={{ color: UNDER_OUTAGE }} />
                        </span>
                      )}
                      {/* mig 371 — travel and stand-down share a hue, so the chip
                          is what tells them apart. Acceptance carries its result. */}
                      {isTravel && (
                        <span className="shrink-0 inline-flex items-center justify-center w-[15px] h-[15px] rounded-full" style={{ background: "rgba(120,53,15,.16)" }}
                          title="TRAVEL — a committed Coretel crew-day, but no site progress in it">
                          <Car size={9.5} style={{ color: TRAVEL }} />
                        </span>
                      )}
                      {isStanddown && (
                        <span className="shrink-0 inline-flex items-center justify-center w-[15px] h-[15px] rounded-full" style={{ background: "rgba(120,53,15,.16)" }}
                          title="STAND-DOWN — paid and committed, zero progress">
                          <Coffee size={9.5} style={{ color: TRAVEL }} />
                        </span>
                      )}
                      {isAcceptance && (
                        <span className="shrink-0 inline-flex items-center justify-center w-[15px] h-[15px] rounded-full"
                          style={{ background: a.acceptance_result === "fail" ? "rgba(193,58,64,.16)"
                            : a.acceptance_result === "pass" ? "rgba(63,150,83,.16)" : "rgba(124,58,237,.16)" }}
                          title={a.acceptance_result === "pass" ? "ACCEPTANCE — passed"
                            : a.acceptance_result === "fail" ? "ACCEPTANCE — FAILED, back to site"
                            : "ACCEPTANCE — a measured window on our delivered work; still open"}>
                          {a.acceptance_result === "pass" ? <BadgeCheck size={9.5} className="text-ok" />
                            : a.acceptance_result === "fail" ? <BadgeX size={9.5} className="text-danger" />
                            : <Clock size={9.5} style={{ color: ACCEPTANCE }} />}
                        </span>
                      )}
                      {isNight && (
                        <span className="shrink-0 inline-flex items-center justify-center w-[15px] h-[15px] rounded-full" style={{ background: "rgba(70,84,140,.16)" }} title="Night shift — crosses midnight">
                          <Moon size={9.5} style={{ color: "#46548c" }} fill="currentColor" />
                        </span>
                      )}
                      {/* Bold follows the Key-date tick ONLY. A plant or outage line
                          is not automatically important — that call is James's, and
                          bolding by kind was half of why "it all looks like it". */}
                      <span className={`text-[12.5px] truncate flex-1 min-w-0 ${a.is_key ? "font-semibold" : external ? "text-text-muted" : ""}`}>{a.name}</span>
                      {/* mig 348 — build-plan state, visible and one-click ON THE GANTT:
                          filled hard-hat = crew sees this line on their build plan;
                          ghosted = they don't. Click flips it. */}
                      <button
                        onClick={(e) => { e.stopPropagation(); onTogglePlan(a); }}
                        className={`shrink-0 p-0.5 rounded transition-colors ${
                          a.in_build_plan
                            ? "text-accent hover:bg-accent/15"
                            : "text-text-dim/45 hover:text-text-muted hover:bg-ink-raised"}`}
                        title={a.in_build_plan
                          ? "ON the crew's build plan — click to take it off"
                          : "NOT on the crew's build plan — click to put it on"}
                        aria-label="Toggle field build plan"
                      >
                        <HardHat size={11} fill={a.in_build_plan ? "currentColor" : "none"} />
                      </button>
                      {a.booking_id && bookingById.has(a.booking_id) && (
                        <Link2 size={10} className="shrink-0 text-accent"
                          aria-label="Linked to a booking" />
                      )}
                      {!external && a.kind === "activity" && a.start_date && !hasCrewOver(a) && (
                        <span className="shrink-0 text-[9px] px-1 rounded border border-amber-500/50 text-amber-600"
                          title="No crew is booked over these dates — book one on the Schedule">no crew</span>
                      )}
                      {/* mig 358 — one-click line complete, right on the row
                          (James: "a bit tough to go into line after line").
                          Ghosted tick = open, filled = done; click flips it. */}
                      {a.track_progress && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onToggleDone(a); }}
                          className={`shrink-0 text-[9px] px-1 rounded border transition-colors ${
                            a.done
                              ? "border-emerald-500/50 text-emerald-700 bg-emerald-500/10"
                              : "border-line text-text-dim/60 hover:text-emerald-700 hover:border-emerald-500/40"}`}
                          title={a.done
                            ? `Line complete${a.done_at ? ` — ${new Date(a.done_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}` : ""} — click to reopen`
                            : "Mark this line complete"}
                          aria-label="Toggle line complete"
                        >
                          {a.done ? "✓ done" : "✓"}
                        </button>
                      )}
                      {(() => {
                        // mig 358 — sub-activity count badge (done lines carry
                        // the tick button above instead; track off = nothing).
                        if (a.done) return null;
                        const subs = subsByActivity.get(a.activity_id) ?? [];
                        if (!subs.length || !a.track_progress) return null;
                        const doneN = subs.filter((x) => x.done).length;
                        const all = doneN === subs.length;
                        return (
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleOpen(a.activity_id); }}
                            className={`shrink-0 text-[9px] px-1 rounded border tabular-nums ${all ? "border-emerald-500/50 text-emerald-700 bg-emerald-500/10" : "border-line text-text-muted"}`}
                            title={`${doneN} of ${subs.length} sub-activities done — click to ${openSubs.has(a.activity_id) ? "hide" : "show"}`}
                          >
                            ☑ {doneN}/{subs.length}
                          </button>
                        );
                      })()}
                      {a.plant_needed && a.plant_needed.length > 0 && (
                        plantCovered(a) ? (
                          <span className="shrink-0 inline-flex items-center gap-0.5 text-[9px] px-1 rounded bg-emerald-500/10 text-emerald-700 border border-emerald-500/40"
                            title={`Needs: ${a.plant_needed.join(", ")} — a plant booking covers these dates`}>
                            <Truck size={9} /> {a.plant_needed.length}
                          </span>
                        ) : (
                          <span className="shrink-0 inline-flex items-center gap-0.5 text-[9px] px-1 rounded bg-amber-500/10 text-amber-700 border border-amber-500/50"
                            title={`Needs: ${a.plant_needed.join(", ")} — NO plant booking covers these dates`}>
                            <Truck size={9} /> {a.plant_needed.join(" · ")}
                          </span>
                        )
                      )}
                    </div>
                    <div className="relative" style={{ width: totalDays * DAY_W }}>
                      {/* weekend + today wash */}
                      {days.map(({ idx, d }) => {
                        const we = d.getDay() === 0 || d.getDay() === 6;
                        if (!we && idx !== todayIdx) return null;
                        return <div key={idx} className="absolute top-0 bottom-0"
                          style={{ left: idx * DAY_W, width: DAY_W,
                            background: idx === todayIdx ? "color-mix(in srgb, var(--accent, #e2661a) 10%, transparent)" : "rgba(127,127,127,.06)",
                            boxShadow: idx === todayIdx ? "inset 2px 0 0 color-mix(in srgb, var(--accent, #e2661a) 55%, transparent)" : undefined }} />;
                      })}
                      {sIdx !== null && (
                        isMilestone ? (
                          <div
                            className="absolute cursor-pointer flex items-center gap-1"
                            style={{ left: sIdx * DAY_W + DAY_W / 2 - 8, top: ROW_H / 2 - 8 }}
                            onMouseDown={(e) => beginDrag(e, a, "move")}
                            title={`${a.name} — ${fmtDate(a.start_date)}`}
                          >
                            <Diamond size={16} fill={laneColor(a.lane)} color={laneColor(a.lane)} />
                            <span className="text-[10.5px] font-bold whitespace-nowrap" style={{ color: laneColor(a.lane) }}>
                              {a.name.length > 28 ? a.name.slice(0, 27) + "…" : a.name}
                            </span>
                          </div>
                        ) : (
                          <div
                            className="absolute rounded flex items-center group"
                            style={{
                              // A night shift starts in the evening of its start
                              // day and finishes the next morning, so the bar is
                              // pushed 60% into the day and runs out the far side
                              // — it visibly STRADDLES midnight instead of sitting
                              // in one column looking like any other day's work
                              // (James, 13 Sep, against the NBN schedule). Drag
                              // maths is delta-based, so the offset doesn't
                              // disturb moving or stretching the bar.
                              left: (sIdx + (isNight ? 0.6 : 0)) * DAY_W + 1,
                              width: nDays * DAY_W - 2,
                              top: 5, bottom: 5,
                              background: isSA
                                ? "repeating-linear-gradient(135deg, #d05252, #d05252 6px, #b34444 6px, #b34444 12px)"
                                : isNSA
                                ? "repeating-linear-gradient(135deg, #7fa8d4, #7fa8d4 6px, #6f97c2 6px, #6f97c2 12px)"
                                // mig 368 — the work done while the network is
                                // down. Beats the lane colour: on a client's
                                // chart this is the thing to see.
                                : underOutage
                                ? UNDER_OUTAGE
                                : isPlant
                                ? "#c9a227"
                                : isAcceptance
                                ? ACCEPTANCE
                                : isTravel
                                ? TRAVEL
                                : isStanddown
                                ? STANDDOWN_BG
                                : isHold
                                ? "repeating-linear-gradient(135deg, rgba(138,149,163,.20), rgba(138,149,163,.20) 5px, transparent 5px, transparent 10px)"
                                : external || isWindow
                                ? `color-mix(in srgb, ${laneColor(a.lane)} 14%, transparent)`
                                : laneColor(a.lane),
                              border: isOutage || isPlant || isAcceptance || isTravel ? "none"
                                : isStanddown ? "1px solid rgba(120,53,15,.45)"
                                : isHold ? "1px solid rgba(138,149,163,.6)"
                                : external || isWindow ? `1.5px dashed ${laneColor(a.lane)}` : "none",
                              boxShadow: isPlant || isOutage ? "0 0 0 1.5px rgba(0,0,0,.15) inset" : undefined,
                              opacity: preview ? 0.75 : 1,
                              cursor: "grab",
                            }}
                            onMouseDown={(e) => beginDrag(e, a, "move")}
                          >
                            {/* mig 358 — progress shading: the completed share of the
                                line reads as a darker fill from the left. James: "show
                                on the gantt progress… nice to claim and show that in
                                the gantt to the client." */}
                            {pct !== null && pct > 0 && (
                              <div
                                className="absolute left-0 top-0 bottom-0 rounded-l pointer-events-none"
                                style={{
                                  width: `${pct}%`,
                                  background: "rgba(0,0,0,.30)",
                                  borderRight: pct < 100 ? "1.5px solid rgba(255,255,255,.55)" : "none",
                                }}
                                title={`${pct}% complete`}
                              />
                            )}
                            <div className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize" onMouseDown={(e) => beginDrag(e, a, "l")} />
                            <div className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize" onMouseDown={(e) => beginDrag(e, a, "r")} />
                            <span className={`px-2 text-[11px] font-medium truncate ${isPlant || isOutage || underOutage || isAcceptance || isTravel ? "text-white font-semibold" : isHold ? "text-text-muted" : external || isWindow ? "" : "text-white"}`}
                              style={isStanddown ? { color: "#6b3410" }
                                : !isPlant && !isOutage && !underOutage && !isHold && !isAcceptance && !isTravel && (external || isWindow) ? { color: laneColor(a.lane) } : undefined}>
                              {nDays * DAY_W > 70 ? a.name : ""}
                            </span>
                          </div>
                        )
                      )}
                      {/* a short bar can't fit its name — print it beside the
                          bar so 1-day lines aren't anonymous boxes */}
                      {sIdx !== null && !isMilestone && nDays * DAY_W <= 70 && (
                        <span className="absolute text-[10.5px] text-text-muted whitespace-nowrap pointer-events-none"
                          style={{ left: (sIdx + nDays + (isNight ? 0.6 : 0)) * DAY_W + 5, top: "50%", transform: "translateY(-50%)" }}>
                          {a.name.length > 34 ? a.name.slice(0, 33) + "…" : a.name}
                        </span>
                      )}
                      {/* the REAL booking's window under the planned bar —
                          plan vs actual at a glance (mig 343) */}
                      {a.booking_id && bookingById.has(a.booking_id) && (() => {
                        const b = bookingById.get(a.booking_id)!;
                        const bs = idxOf(tsDay(b.scheduled_start));
                        const be = b.scheduled_end ? idxOf(tsDay(b.scheduled_end)) : bs;
                        const mismatch = sIdx !== null && (bs !== sIdx || be !== sIdx + nDays - 1);
                        return (
                          <div
                            className="absolute rounded-sm pointer-events-none"
                            style={{
                              left: bs * DAY_W + 2, width: Math.max((be - bs + 1) * DAY_W - 4, 6),
                              bottom: 1, height: 4,
                              background: mismatch ? "#d05252" : "#37a97f",
                              opacity: 0.9,
                            }}
                            title={`Booking: ${b.title}`}
                          />
                        );
                      })()}
                      {sIdx === null && (
                        <button className="absolute left-2 top-1.5 text-[11px] text-text-muted border border-dashed border-line rounded px-1.5 py-0.5 hover:text-text"
                          onClick={() => onBarClick(a)}>
                          no date — set one
                        </button>
                      )}
                    </div>
                  </div>
                  {openSubs.has(a.activity_id) && subsHere.length > 0 && (
                    <div className="border-t border-dashed border-line/40" style={{ background: "rgba(226,102,26,.03)" }}>
                      {subsHere.map((su) => (
                        <div key={su.sub_id} className="flex items-center" style={{ height: 24 }}>
                          <div style={{ width: LABEL_W }} className="shrink-0 pl-12 pr-3 flex items-center gap-2 sticky left-0 z-10 bg-ink-card border-r border-line/40 h-full">
                            <input
                              type="checkbox"
                              checked={su.done}
                              onChange={() => onToggleSub(su)}
                              className="accent-accent cursor-pointer"
                            />
                            {su.crew && (
                              <span className={`shrink-0 text-[9px] font-semibold px-1 rounded ${su.crew === "tech" ? "bg-sky-600/15 text-sky-700" : su.crew === "civil" ? "bg-amber-700/15 text-amber-800" : "bg-accent/15 text-accent"}`}
                                title={`${CREW_LABEL[su.crew]} — goes to the ${su.crew === "tech" ? "Technician" : "Riggers"} row on the build plan`}>
                                {CREW_SHORT[su.crew]}
                              </span>
                            )}
                            <span className={`text-[11.5px] truncate ${su.done ? "line-through text-text-dim" : "text-text-muted"}`}>
                              {su.name}
                            </span>
                          </div>
                          <div className="flex-1 flex items-center pl-2">
                            {su.done && su.done_at && (
                              <span className="text-[10px] text-emerald-700">
                                done {new Date(su.done_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  </React.Fragment>
                );
              })}

            </React.Fragment>
          ))}
        </div>
      </div>
      {/* Legend — the same key the exported PDF carries, so the screen and the
          sent document say the same thing. James, 13 Sep: "if they are always a
          colour fine, but change it or it all looks like it." */}
      <div className="px-3 py-2 border-t border-line flex items-center gap-x-4 gap-y-1.5 flex-wrap text-[11px] text-text-muted">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-6 h-3.5 rounded-sm" style={{ boxShadow: "inset 3px 0 0 rgba(226,102,26,.9)", background: "rgba(226,102,26,.05)" }} />
          <span className="font-semibold text-text">Key date</span> — your call
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-flex items-center justify-center w-[15px] h-[15px] rounded-full bg-danger/15"><Zap size={9.5} className="text-danger" fill="currentColor" /></span>
          Outage — <span className="font-semibold text-text">service affecting</span>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-flex items-center justify-center w-[15px] h-[15px] rounded-full bg-info/15"><Zap size={9.5} className="text-info" /></span>
          Outage — NSA (routine)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-flex items-center justify-center w-[15px] h-[15px] rounded-full" style={{ background: "rgba(70,84,140,.16)" }}><Moon size={9.5} style={{ color: "#46548c" }} fill="currentColor" /></span>
          Night shift — bar straddles midnight
        </span>
        {/* mig 368 — the bar itself goes emerald, so the key shows the bar, not a chip alone. */}
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-6 h-3 rounded-sm" style={{ background: UNDER_OUTAGE }} />
          <span className="font-semibold text-text">Work under outage</span>
        </span>
        {/* mig 371 — travel and stand-down share one hue on purpose: the colour
            says "crew-day, no build", the chip says which. */}
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-6 h-3 rounded-sm" style={{ background: TRAVEL }} />
          <Car size={10} style={{ color: TRAVEL }} /> Travel
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-6 h-3 rounded-sm" style={{ background: STANDDOWN_BG, border: "1px solid rgba(120,53,15,.45)" }} />
          <Coffee size={10} style={{ color: TRAVEL }} /> Stand-down — paid, no progress
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-6 h-3 rounded-sm" style={{ background: ACCEPTANCE }} />
          Acceptance window <span className="text-text-dim">(pass / fail)</span>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-flex items-center justify-center w-[15px] h-[15px] rounded-full bg-warn/15"><Truck size={9.5} className="text-warn" /></span>
          Plant on site
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-flex items-center justify-center w-[15px] h-[15px] rounded-full bg-accent/15"><Diamond size={9} className="text-accent" fill="currentColor" /></span>
          Milestone
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-6 h-3 rounded-sm bg-accent" /> Coretel on the tools
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-6 h-3 rounded-sm border border-dashed border-text-muted" /> Client / subbie — we work around it
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-6 h-3 rounded-sm" style={{
            border: "1px solid rgba(138,149,163,.6)",
            background: "repeating-linear-gradient(135deg, rgba(138,149,163,.20), rgba(138,149,163,.20) 4px, transparent 4px, transparent 8px)",
          }} /> Hold / buffer — ours, deliberately empty
        </span>
      </div>
      <div className="px-3 pb-2 text-[11.5px] text-text-muted">
        Drag a bar to move it · drag an end to stretch it · click to edit everything.
        Changes save as you go — use Import for a whole new revision from Claude or Excel.
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// EditActivityModal — every field on one line, plus delete
// ---------------------------------------------------------------------------
function EditActivityModal({ activity, lanes, bookings, isBid, subs, onSaveSubs, onLinkBooking, onCreateBooking, onClose, onSave, onDelete }: {
  activity: ProgramActivity;
  lanes: ProgramLane[];
  bookings: BookingRow[];
  isBid: boolean;
  subs: SubRow[];
  onSaveSubs: (names: string[]) => void;
  onLinkBooking: (bookingId: string | null) => void;
  onCreateBooking: () => void;
  onClose: () => void;
  onSave: (patch: Partial<ProgramActivity>) => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const o = parseOwner(activity.owner);
  const [name, setName] = useState(activity.name);
  const [lane, setLane] = useState(activity.lane ?? "");
  const [kind, setKind] = useState(activity.kind);
  const [ownerType, setOwnerType] = useState<"internal" | "client" | "subbie">(o.type === "internal" ? "internal" : o.type === "client" ? "client" : "subbie");
  const [ownerName, setOwnerName] = useState(o.type === "internal" ? "" : o.name);
  const [resource, setResource] = useState(activity.resource ?? "");
  const [start, setStart] = useState(activity.start_date ?? "");
  const [daysN, setDaysN] = useState(String(activity.days ?? 1));
  const [notes, setNotes] = useState(activity.notes ?? "");
  const [visible, setVisible] = useState(activity.client_visible);
  const [isKey, setIsKey] = useState(activity.is_key);
  const [inPlan, setInPlan] = useState(activity.in_build_plan);
  const [trackProg, setTrackProg] = useState(activity.track_progress ?? true);  // mig 358
  const [lineDone, setLineDone] = useState(activity.done ?? false);
  const [night, setNight] = useState(activity.night_shift ?? false);
  const [underOutage, setUnderOutage] = useState(activity.under_outage ?? false);
  // mig 371 — "" is the honest default: the KPI window hasn't closed yet.
  const [acceptResult, setAcceptResult] = useState<"" | "pass" | "fail">(activity.acceptance_result ?? "");
  const [impact, setImpact] = useState<"nsa" | "sa">(outageImpact(activity) ?? "sa");
  const [plant, setPlant] = useState<string[]>(activity.plant_needed ?? []);
  const [plantDraft, setPlantDraft] = useState("");
  // mig 381 — each sub carries its crew. Typing "Tech: terminate hybrids" in the
  // box sets the chip; the chip sets it after. Saved as "<Crew>: name" strings,
  // which is exactly what the importer and the Projects Claude speak.
  const [subItems, setSubItems] = useState<{ name: string; crew: SubCrew }[]>(subs.map((x) => ({ name: x.name, crew: x.crew ?? null })));
  const subNames = subItems.map((x) => x.name);
  const [subDraft, setSubDraft] = useState("");
  const subsDirty = JSON.stringify(subItems) !== JSON.stringify(subs.map((x) => ({ name: x.name, crew: x.crew ?? null })));
  const doneByName = new Map(subs.map((x) => [x.name.toLowerCase(), x.done]));
  const setSubCrew = (i: number, crew: SubCrew) => setSubItems((prev) => prev.map((x, j) => (j === i ? { ...x, crew } : x)));
  const [busy, setBusy] = useState(false);

  function addPlant(raw: string) {
    const items = raw.split(/[,;+]/).map((x) => x.trim()).filter(Boolean);
    if (!items.length) return;
    setPlant((prev) => [...prev, ...items.filter((x) => !prev.some((p) => p.toLowerCase() === x.toLowerCase()))]);
    setPlantDraft("");
  }

  // bookings whose window touches this line's dates float to the top of the picker
  const lineEnd = activity.end_date ?? activity.start_date;
  const overlapsLine = (b: BookingRow): boolean => {
    if (!activity.start_date || !lineEnd) return false;
    const bs = tsDay(b.scheduled_start);
    const be = b.scheduled_end ? tsDay(b.scheduled_end) : bs;
    return bs <= lineEnd && be >= activity.start_date;
  };
  const sortedBookings = [...bookings].sort((x, y) =>
    Number(overlapsLine(y)) - Number(overlapsLine(x))
    || x.scheduled_start.localeCompare(y.scheduled_start));

  async function save() {
    setBusy(true);
    await onSave({
      name: name.trim() || activity.name,
      lane: lane || null,
      kind: kind as any,
      owner: ownerType === "internal" ? "internal" : `${ownerType}:${(ownerName || "External").trim()}`,
      resource: resource.trim() || null,
      start_date: start || null,
      days: Math.max(parseInt(daysN, 10) || 1, 1),
      notes: notes.trim() || null,
      client_visible: visible,
      plant_needed: plant,
      is_key: isKey,
      in_build_plan: inPlan,
      track_progress: trackProg,
      done: trackProg ? lineDone : false,  // untracked lines can't be "done"
      night_shift: night,
      under_outage: underOutage,
      // a result only means something on an acceptance window
      acceptance_result: kind === "acceptance" ? (acceptResult || null) : null,
      // Only an outage carries an impact — changing a line's kind away from
      // outage clears it rather than leaving a stale classification behind.
      outage_impact: kind === "outage" ? impact : null,
    } as Partial<ProgramActivity>);
    if (subsDirty) onSaveSubs(subItems.filter((x) => x.name.trim()).map((x) => encodeSub(x.name.trim(), x.crew)));
    setBusy(false);
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center pt-14 px-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-ink-card rounded-xl border border-line w-full max-w-4xl mb-10" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-line">
          <div className="text-[14px] font-semibold">Edit activity <span className="text-text-muted font-normal">· {activity.code}</span></div>
          <button onClick={onClose} className="text-text-muted hover:text-text"><X size={16} /></button>
        </div>
        {/* Two columns from lg up (James, 13 Sep: "all cramped up... a lot of
            information and details nested so small"). Left = what the line IS;
            right = the detail that needs room to read — subs and the booking. */}
        <div className="p-5">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-5 gap-y-3 items-start">
        <div className="space-y-3 min-w-0">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-text-muted mb-1">Activity</div>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-text-muted mb-1">Lane</div>
              <select value={lane} onChange={(e) => setLane(e.target.value)} className="w-full text-[13px] bg-transparent border border-line rounded-lg px-2 py-1.5">
                <option value="">—</option>
                {lanes.map((l) => <option key={l.lane_code} value={l.lane_code}>{l.lane_code}</option>)}
                {lane && !lanes.some((l) => l.lane_code === lane) && <option value={lane}>{lane}</option>}
              </select>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-text-muted mb-1">Type</div>
              <select value={kind} onChange={(e) => setKind(e.target.value as any)} className="w-full text-[13px] bg-transparent border border-line rounded-lg px-2 py-1.5">
                <option value="activity">Activity</option>
                <option value="milestone">Milestone</option>
                <option value="hold">Hold / no work</option>
                <option value="outage">Outage window</option>
                <option value="plant">Plant</option>
                <option value="client_deliverable">Client deliverable</option>
                {/* mig 371 */}
                <option value="acceptance">Acceptance window (pass / fail)</option>
                <option value="travel">Travel / mobilisation</option>
                <option value="standdown">Stand-down</option>
              </select>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-text-muted mb-1">Who</div>
              <div className="flex gap-1.5">
                <select value={ownerType} onChange={(e) => setOwnerType(e.target.value as any)} className="text-[13px] bg-transparent border border-line rounded-lg px-1.5 py-1.5 w-full">
                  <option value="internal">Coretel</option>
                  <option value="client">Client</option>
                  <option value="subbie">Subbie</option>
                </select>
              </div>
            </div>
          </div>
          {ownerType !== "internal" && (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-text-muted mb-1">{ownerType === "client" ? "Client name" : "Subbie name"}</div>
              <Input value={ownerName} onChange={(e) => setOwnerName(e.target.value)} placeholder="Hardline" />
            </div>
          )}
          <div className="grid grid-cols-[1fr_84px] gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-text-muted mb-1">Start</div>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-text-muted mb-1">Days</div>
              <Input type="number" min={1} value={daysN} onChange={(e) => setDaysN(e.target.value)} />
            </div>
          </div>
          {/* Resource gets the full width — the crew shape is the thing a reader
              most wants off a line, and it was clipping at a third. */}
          <div>
            <div className="text-[11px] uppercase tracking-wide text-text-muted mb-1">Resource</div>
            <Input value={resource} onChange={(e) => setResource(e.target.value)} placeholder="2 × 3 — 2 riggers + 1 tech each end" />
          </div>
          {/* mig 366 — two facts the chart can't guess at. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="flex items-start gap-2 text-[13px] border border-line rounded-lg px-3 py-2.5"
              title="The work crosses midnight. The bar is drawn straddling the day boundary instead of sitting inside one day.">
              <input type="checkbox" className="mt-0.5" checked={night} onChange={(e) => setNight(e.target.checked)} />
              <span>
                <span className="inline-flex items-center gap-1.5"><Moon size={11} style={{ color: "#46548c" }} fill="currentColor" /> Night shift</span>
                <div className="text-[11px] text-text-muted">Crosses midnight — the bar straddles the two days.</div>
              </span>
            </label>
            {/* mig 368 */}
            <label className="flex items-start gap-2 text-[13px] border rounded-lg px-3 py-2.5"
              style={underOutage ? { borderColor: UNDER_OUTAGE, background: "rgba(18,128,92,.06)" } : undefined}
              title="Work performed inside a service-affecting outage window — the network is down while this happens. Drawn in emerald so the client can see it on the chart.">
              <input type="checkbox" className="mt-0.5" checked={underOutage} onChange={(e) => setUnderOutage(e.target.checked)} />
              <span>
                <span className="inline-flex items-center gap-1.5">
                  <AlertTriangle size={11} style={{ color: UNDER_OUTAGE }} /> Outage activity
                </span>
                <div className="text-[11px] text-text-muted">Done under an SA CRQ — customers are off air while this runs.</div>
              </span>
            </label>
            {kind === "outage" && (
              <div className="border border-line rounded-lg px-3 py-2.5">
                <div className="text-[11px] uppercase tracking-wide text-text-muted mb-1">Outage impact</div>
                <select value={impact} onChange={(e) => setImpact(e.target.value as any)}
                  className="w-full text-[13px] bg-transparent border border-line rounded-lg px-2 py-1.5">
                  <option value="sa">SA — service affecting (customers off air)</option>
                  <option value="nsa">NSA — non service affecting (routine cover)</option>
                </select>
                <div className="text-[11px] text-text-muted mt-1">
                  {impact === "sa"
                    ? "Red bar, and the row is shaded — this one has to be seen."
                    : "Quiet blue bar, no row shading — standard CRQ cover."}
                </div>
              </div>
            )}
            {/* mig 371 — the acceptance KPI on work we have delivered. It is
                not labour and it is not a third party's bar: it passes or it
                fails, and a fail means going back to site. */}
            {kind === "acceptance" && (
              <div className="border rounded-lg px-3 py-2.5"
                style={{ borderColor: acceptResult === "fail" ? "rgb(var(--danger))"
                  : acceptResult === "pass" ? "rgb(var(--ok))" : ACCEPTANCE }}>
                <div className="text-[11px] uppercase tracking-wide text-text-muted mb-1">Acceptance result</div>
                <select value={acceptResult} onChange={(e) => setAcceptResult(e.target.value as any)}
                  className="w-full text-[13px] bg-transparent border border-line rounded-lg px-2 py-1.5">
                  <option value="">Still open — the window hasn't closed</option>
                  <option value="pass">Passed</option>
                  <option value="fail">Failed — back to site</option>
                </select>
                <div className="text-[11px] text-text-muted mt-1">
                  {acceptResult === "fail"
                    ? "Flagged on the chart in red. Whatever it takes to re-test belongs on the programme as real lines."
                    : acceptResult === "pass"
                    ? "Flagged green. The link is accepted."
                    : "Re-importing the programme never overwrites a result you have recorded here."}
                </div>
              </div>
            )}
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-text-muted mb-1">Notes</div>
            <Textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="CRANE DUTY 1 · road closure 08:30" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-text-muted mb-1 flex items-center gap-1.5">
              <Truck size={11} /> Plant needed
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {plant.map((pl) => (
                <span key={pl} className="inline-flex items-center gap-1 text-[12px] px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/40 text-amber-800">
                  {pl}
                  <button onClick={() => setPlant((prev) => prev.filter((x) => x !== pl))} className="hover:text-danger"><X size={10} /></button>
                </span>
              ))}
              <Input
                value={plantDraft}
                onChange={(e) => setPlantDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addPlant(plantDraft); } }}
                onBlur={() => addPlant(plantDraft)}
                placeholder={plant.length ? "add more…" : "type your own…"}
                className="w-36"
              />
            </div>
            <div className="flex items-center gap-1.5 mt-1.5">
              {["Crane", "EWP", "Franna", "Excavator"].filter((x) => !plant.some((p) => p.toLowerCase().startsWith(x.toLowerCase()))).map((x) => (
                <button key={x} onClick={() => addPlant(x)}
                  className="text-[11px] px-2 py-0.5 rounded-full border border-line text-text-muted hover:border-amber-500/60 hover:text-amber-700">
                  + {x}
                </button>
              ))}
            </div>
            <div className="text-[11px] text-text-muted mt-1">
              Shows on the Gantt — amber until a plant booking covers these dates, green once one does.
            </div>
          </div>
        </div>

        <div className="space-y-3 min-w-0">
          <div className="border border-line rounded-lg px-3 py-2.5">
            <div className="text-[11px] uppercase tracking-wide text-text-muted mb-1.5 flex items-center gap-1.5">
              ☑ Sub-activities <span className="normal-case tracking-normal">— detail for the crew; never on client exports</span>
            </div>
            {subNames.length > 0 && (
              /* Sub names WRAP — they carry the real instruction ("Validate RSL
                 on the 11 GHz link — the link is in bearing, so no re-pan"),
                 and truncating them hid exactly the part that matters. */
              <div className="space-y-1.5 mb-1.5 max-h-[340px] overflow-y-auto pr-1">
                {subItems.map((it, i) => {
                  const nm = it.name;
                  return (
                  <div key={i} className="flex items-start gap-2 text-[13px]">
                    <span className={`w-3 shrink-0 text-center text-[11px] leading-5 ${doneByName.get(nm.toLowerCase()) ? "text-emerald-600" : "text-text-dim"}`}>
                      {doneByName.get(nm.toLowerCase()) ? "✓" : "·"}
                    </span>
                    <span className="flex-1 min-w-0 leading-5 break-words">{nm}</span>
                    {/* mig 381 — who does this step. R = riggers (tower / external), T = tech (cabinet), C = civil, — = the lane decides */}
                    <div className="shrink-0 flex rounded border border-line overflow-hidden" title="Which crew does this step — drives the weekly build plan rows">
                      {([null, "riggers", "tech", "civil"] as SubCrew[]).map((c) => (
                        <button key={c ?? "none"} type="button" onClick={() => setSubCrew(i, c)}
                          className={`px-1.5 text-[10px] leading-5 ${it.crew === c ? (c === "tech" ? "bg-sky-600 text-white" : c === "civil" ? "bg-amber-700 text-white" : c === "riggers" ? "bg-accent text-white" : "bg-ink-hover text-text") : "text-text-dim hover:text-text"}`}>
                          {c ? CREW_SHORT[c] : "—"}
                        </button>
                      ))}
                    </div>
                    <button onClick={() => setSubItems((prev) => i > 0 ? [...prev.slice(0, i - 1), prev[i], prev[i - 1], ...prev.slice(i + 1)] : prev)}
                      className="shrink-0 mt-0.5 text-text-dim hover:text-text" title="Move up"><ArrowUp size={11} /></button>
                    <button onClick={() => setSubItems((prev) => prev.filter((_, j) => j !== i))}
                      className="shrink-0 mt-0.5 text-text-dim hover:text-danger" title="Remove"><X size={11} /></button>
                  </div>
                  );
                })}
              </div>
            )}
            <Input
              value={subDraft}
              onChange={(e) => setSubDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && subDraft.trim()) {
                  e.preventDefault();
                  const sp = splitSubCrew(subDraft);
                  setSubItems((prev) => [...prev, { name: sp.name, crew: sp.crew === "" ? null : sp.crew }]);
                  setSubDraft("");
                }
              }}
              placeholder={subNames.length ? "add another… (Enter) — start with Riggers: / Tech: / Civil: to tag it" : "e.g. Riggers: install 9 antennas on ground (Enter to add)"}
            />
            {subsDirty && <div className="text-[11px] text-amber-700 mt-1">Changes save when you press Save. Renaming a sub resets its tick; changing its crew does not.</div>}
          </div>

          {/* mig 369 — a BID has no bookings: nothing is scheduled against a job
              we haven't won, and project_bookings.project_id would have nothing
              to point at. The whole block goes rather than showing an empty
              picker and a button that would fail. */}
          {!isBid && (kind === "outage" || kind === "plant" || kind === "activity") && (
            <div className="border border-line rounded-lg px-3 py-2.5">
              <div className="text-[11px] uppercase tracking-wide text-text-muted mb-1.5 flex items-center gap-1.5">
                <Link2 size={11} /> Real booking
              </div>
              {activity.booking_id && bookings.some((b) => b.booking_id === activity.booking_id) ? (() => {
                const b = bookings.find((x) => x.booking_id === activity.booking_id)!;
                return (
                  <div className="flex items-center gap-2 text-[13px]">
                    <span className="min-w-0 truncate">
                      {b.title} <span className="text-text-muted">· {b.booking_type} · {b.status} ·{" "}
                      {new Date(b.scheduled_start).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                    </span>
                    <button className="ml-auto shrink-0 text-[12px] text-text-muted hover:text-danger inline-flex items-center gap-1"
                      onClick={() => onLinkBooking(null)}><Unlink size={11} /> Unlink</button>
                  </div>
                );
              })() : (
                <div className="flex items-center gap-2 flex-wrap">
                  <select
                    className="text-[12.5px] bg-transparent border border-line rounded px-1.5 py-1 w-full"
                    defaultValue=""
                    onChange={(e) => { if (e.target.value) onLinkBooking(e.target.value); }}
                  >
                    <option value="" disabled>Link an existing booking…</option>
                    {sortedBookings.map((b) => (
                      <option key={b.booking_id} value={b.booking_id}>
                        {overlapsLine(b) ? "◉ " : ""}{b.title} · {b.booking_type} · {new Date(b.scheduled_start).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}{overlapsLine(b) ? " · overlaps these dates" : ""}
                      </option>
                    ))}
                  </select>
                  {(kind === "outage" || kind === "plant") && activity.start_date && (
                    <Button variant="ghost" size="sm" icon={Plus} onClick={onCreateBooking}>
                      Create booking from this line
                    </Button>
                  )}
                </div>
              )}
              <div className="text-[11px] text-text-muted mt-1.5">
                The booking carries the real times, contacts and approvals. Dragging a linked line asks before the booking moves.
              </div>
            </div>
          )}
        </div>
        </div>

        <div className="mt-4 pt-3 border-t border-line/60">
          <div className="flex items-center gap-5 flex-wrap">
            <label className="flex items-center gap-2 text-[13px]">
              <input type="checkbox" checked={visible} onChange={(e) => setVisible(e.target.checked)} />
              Client can see this line (exports &amp; portal)
            </label>
            <label className="flex items-center gap-2 text-[13px]" title="Highlights the row on the Gantt and the client PDF — your call, not automatic">
              <input type="checkbox" checked={isKey} onChange={(e) => setIsKey(e.target.checked)} />
              <span className="text-accent">★</span> Key date (highlighted)
            </label>
            <label className="flex items-center gap-2 text-[13px]" title="Shows on the crew's build plan — My schedule and Today's Work (mig 348). Coretel-owned lines default in; subbie/client lines default out; adding sub-activities pulls a line in.">
              <input type="checkbox" checked={inPlan} onChange={(e) => setInPlan(e.target.checked)} />
              Field build plan
            </label>
            {/* mig 358 — line progress */}
            <label className="flex items-center gap-2 text-[13px]" title="Off = this line carries no progress: no Gantt shading, no badge, left out of the progress story (holds, client waits, admin lines).">
              <input type="checkbox" checked={trackProg} onChange={(e) => setTrackProg(e.target.checked)} />
              Track progress
            </label>
            <label className={`flex items-center gap-2 text-[13px] ${trackProg ? "" : "opacity-40"}`} title="Mark the whole line complete — shades the bar full on the Gantt and the client PDF. Who/when is stamped automatically.">
              <input type="checkbox" checked={lineDone} disabled={!trackProg} onChange={(e) => setLineDone(e.target.checked)} />
              <span className="text-emerald-700">✓</span> Line complete
            </label>
          </div>
          <div className="flex items-center justify-between pt-3">
            <Button variant="ghost" size="sm" icon={Trash2} onClick={() => { if (confirm("Remove this line from the program? Its history is kept.")) void onDelete(); }}>
              Remove
            </Button>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
              <Button size="sm" icon={Check} onClick={() => void save()} disabled={busy}>{busy ? "Saving…" : "Save"}</Button>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// OwnerChip — the Who column, click to fix (James, 2 Sep: "civils on this is
// Hardline not Coretel"). Coretel = solid accent; anyone else = outlined with
// client/subbie picked here. Saves straight to the row; a re-import keeps it.
// ---------------------------------------------------------------------------
function OwnerChip({ activity, projectId, parentKind }: { activity: ProgramActivity; projectId: string; parentKind: "project" | "bid" }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const o = parseOwner(activity.owner);
  const [type, setType] = useState<"internal" | "client" | "subbie">(o.type === "internal" ? "internal" : o.type === "client" ? "client" : "subbie");
  const [name, setName] = useState(o.type === "internal" ? "" : o.name);

  async function save() {
    const owner = type === "internal" ? "internal" : `${type}:${(name || "External").trim()}`;
    if (owner === activity.owner) { setOpen(false); return; }
    setSaving(true);
    const { error } = await supabase
      .from("project_program_activities")
      .update({ owner, updated_at: new Date().toISOString() })
      .eq("activity_id", activity.activity_id);
    setSaving(false);
    if (error) { alert(`Couldn't save: ${error.message}`); return; }
    setOpen(false);
    qc.invalidateQueries({ queryKey: ["program", parentKind, projectId] });
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        title="Who does this line? Click to change."
        className={o.type === "internal"
          ? "text-[11px] px-1.5 py-0.5 rounded bg-accent/10 text-accent hover:bg-accent/20"
          : "text-[11px] px-1.5 py-0.5 rounded border border-line text-text-muted hover:border-text-muted"}
      >
        {o.name}
        {o.type === "client" && <span className="opacity-60"> · client</span>}
        {o.type === "subbie" && <span className="opacity-60"> · subbie</span>}
      </button>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      <select
        value={type}
        onChange={(e) => setType(e.target.value as any)}
        className="text-[12px] bg-transparent border border-line rounded px-1 py-0.5"
      >
        <option value="internal">Coretel</option>
        <option value="client">Client</option>
        <option value="subbie">Subbie</option>
      </select>
      {type !== "internal" && (
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Hardline"
          autoFocus
          className="text-[12px] bg-transparent border border-line rounded px-1.5 py-0.5 w-24"
          onKeyDown={(e) => { if (e.key === "Enter") void save(); if (e.key === "Escape") setOpen(false); }}
        />
      )}
      <button onClick={() => void save()} disabled={saving} className="text-accent"><Check size={13} /></button>
      <button onClick={() => setOpen(false)} className="text-text-muted"><X size={13} /></button>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Import modal: paste / upload → diff review → Apply
// ---------------------------------------------------------------------------
function ImportModal({ projectId, projectCode, isBid, current, subsByActivity, onClose, onApplied }: {
  projectId: string; projectCode: string | null; isBid: boolean;
  current: ProgramActivity[];
  subsByActivity: Map<string, SubRow[]>;
  onClose: () => void; onApplied: () => void;
}) {
  const [text, setText] = useState("");
  const [payload, setPayload] = useState<ProgramPayload | null>(null);
  const [revLabel, setRevLabel] = useState("");
  const [source, setSource] = useState<"paste" | "excel">("paste");
  const [error, setError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const [bookingImpacts, setBookingImpacts] = useState<{ title: string; window: string; hit: string }[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  const [clearStale, setClearStale] = useState(true);

  const diff = useMemo(() => (payload ? diffProgram(current, payload) : null), [payload, current]);
  const hazards = useMemo(
    () => (payload && diff ? staleSubsHazards(diff.lines, subsByActivity, payload) : []),
    [payload, diff, subsByActivity],
  );

  function loadFromText(t: string) {
    setError(null);
    const p = extractProgramJson(t);
    if (!p) { setError("Couldn't find a coretel.program.v1 payload in that — paste the whole JSON block from Claude's reply."); setPayload(null); return; }
    setSource("paste");
    setPayload(p);
    setRevLabel(p.project?.revision ?? "");
    void checkBookings(p);
  }

  async function loadFromFile(f: File) {
    setError(null);
    try {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf, { cellDates: true });
      const p = workbookToPayload(wb);
      if (!p) { setError("Couldn't read a Schedule (or Summary) sheet with Task/Start columns in that workbook."); setPayload(null); return; }
      // A workbook's owner is only inferred from Resource — never let it
      // overwrite a Who that someone set by hand in Ops.
      const curByCode = new Map(current.filter((a) => a.is_active).map((a) => [a.code, a]));
      for (const act of p.activities) {
        const ex = curByCode.get(act.id);
        if (ex && ex.owner !== "internal") act.owner = ex.owner;
      }
      setSource("excel");
      setPayload(p);
      void checkBookings(p);
    } catch (e: any) {
      setError(`Couldn't open that file: ${e?.message ?? e}`);
    }
  }

  /** Which existing bookings sit inside windows this import touches? */
  async function checkBookings(p: ProgramPayload) {
    setBookingImpacts([]);
    const d = diffProgram(current, p);
    const windows: { label: string; from: string; to: string }[] = [];
    for (const line of d.lines) {
      if (line.type === "new" && line.act.start) {
        const end = line.act.end ?? (line.act.days ? addDays(line.act.start, line.act.days - 1) : line.act.start);
        windows.push({ label: line.act.name, from: line.act.start, to: end });
      }
      if (line.type === "changed") {
        const olds = line.before.start_date, olde = line.before.end_date ?? line.before.start_date;
        if (olds) windows.push({ label: `${line.act.name} (old dates)`, from: olds, to: olde ?? olds });
        if (line.act.start) {
          const end = line.act.end ?? (line.act.days ? addDays(line.act.start, line.act.days - 1) : line.act.start);
          windows.push({ label: line.act.name, from: line.act.start, to: end });
        }
      }
      if (line.type === "removed" && line.act.start_date) {
        windows.push({ label: `${line.act.name} (removed)`, from: line.act.start_date, to: line.act.end_date ?? line.act.start_date });
      }
    }
    if (!windows.length) return;
    const from = windows.reduce((m, w) => (w.from < m ? w.from : m), windows[0].from);
    const to = windows.reduce((m, w) => (w.to > m ? w.to : m), windows[0].to);
    // A bid has no bookings to clash with — nothing is scheduled against a
    // job we haven't won.
    if (isBid) return;
    const { data: bookings } = await supabase
      .from("project_bookings")
      .select("title, booking_type, scheduled_start, scheduled_end, status")
      .eq("project_id", projectId)
      .neq("status", "cancelled")
      .lte("scheduled_start", to + "T23:59:59")
      .gte("scheduled_start", from + "T00:00:00");
    if (!bookings?.length) return;
    const impacts: { title: string; window: string; hit: string }[] = [];
    for (const b of bookings) {
      const bs = String(b.scheduled_start).slice(0, 10);
      const hitWindows = windows.filter((w) => bs >= w.from && bs <= w.to);
      if (hitWindows.length) {
        impacts.push({
          title: `${b.title} (${b.booking_type}, ${b.status})`,
          window: fmtDate(bs),
          hit: hitWindows[0].label,
        });
      }
    }
    setBookingImpacts(impacts.slice(0, 12));
  }

  async function apply() {
    if (!payload) return;
    setApplying(true);
    setError(null);
    // Clearing a flagged line's subs is done by making the payload SAY so
    // ("subs": [] clears — proved in prodsim, mig 365) rather than by a second
    // write afterwards, so the whole import stays one atomic RPC call.
    const hazardCodes = new Set(clearStale ? hazards.map((h) => h.code) : []);
    const toSend: ProgramPayload = {
      ...payload,
      activities: hazardCodes.size
        ? payload.activities.map((a) => (hazardCodes.has(a.id) ? { ...a, subs: [] } : a))
        : payload.activities,
      project: { ...(payload.project ?? {}), code: payload.project?.code ?? projectCode ?? undefined, revision: revLabel || payload.project?.revision },
    };
    const { data: result, error: e } = await supabase.schema("app").rpc("program_apply", {
      // mig 369 — exactly one parent; the RPC raises if both or neither arrive.
      p_project_id: isBid ? null : projectId,
      p_bid_id: isBid ? projectId : null,
      p_payload: toSend, p_source: source,
    });
    setApplying(false);
    if (e) { setError(`Apply failed — nothing was changed: ${e.message}`); return; }
    void result;
    onApplied();
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center pt-10 px-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-ink-card rounded-xl border border-line w-full max-w-3xl mb-10" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-line">
          <div className="text-[14px] font-semibold">Import build program</div>
          <button onClick={onClose} className="text-text-muted hover:text-text"><X size={16} /></button>
        </div>

        <div className="p-5 space-y-4">
          {!payload && (
            <>
              <div className="text-[13px] text-text-muted">
                Paste the <span className="font-medium text-text">program JSON</span> from your Projects Claude
                (Build Program directive), or upload a Coretel Gantt workbook — the Schedule sheet reads directly.
                Nothing changes until you press Apply.
              </div>
              <Textarea
                rows={8}
                placeholder='Paste here — the whole reply is fine, I&apos;ll find the {"schema":"coretel.program.v1"...} block in it.'
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
              <div className="flex items-center gap-2">
                <Button size="sm" icon={Clipboard} onClick={() => loadFromText(text)} disabled={!text.trim()}>
                  Read pasted program
                </Button>
                <span className="text-[12px] text-text-muted">or</span>
                <Button size="sm" variant="ghost" icon={FileSpreadsheet} onClick={() => fileRef.current?.click()}>
                  Upload Gantt workbook (.xlsx)
                </Button>
                <input
                  ref={fileRef} type="file" accept=".xlsx,.xlsm" className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) void loadFromFile(f); e.currentTarget.value = ""; }}
                />
              </div>
            </>
          )}

          {error && (
            <div className="text-[13px] text-red-600 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          {payload && diff && (
            <>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="text-[13px]">
                  <span className="font-semibold">{payload.activities.length}</span> activities in the import
                  {diff.unchanged > 0 && <span className="text-text-muted"> · {diff.unchanged} unchanged</span>}
                </div>
                <div className="ml-auto flex items-center gap-2">
                  <span className="text-[12px] text-text-muted">Revision label</span>
                  <Input value={revLabel} onChange={(e) => setRevLabel(e.target.value)} placeholder="v1.2" className="w-28" />
                </div>
              </div>

              {diff.lines.length === 0 ? (
                <div className="text-[13px] text-text-muted border border-line rounded-lg px-3 py-2.5">
                  Identical to the current program — applying will just record a new revision.
                </div>
              ) : (
                <div className="border border-line rounded-lg divide-y divide-line/60 max-h-72 overflow-y-auto">
                  {diff.lines.map((l, i) => (
                    <div key={i} className="px-3 py-2 text-[13px] flex items-start gap-2.5">
                      {l.type === "new" && (
                        <>
                          <span className="text-[10.5px] font-semibold uppercase tracking-wide text-emerald-600 bg-emerald-500/10 rounded px-1.5 py-0.5 mt-px shrink-0">New</span>
                          <span>{l.act.name}
                            <span className="text-text-muted"> · {l.act.lane ?? "—"} · {fmtDate(l.act.start)}{l.act.days ? ` · ${l.act.days}d` : ""}</span>
                          </span>
                        </>
                      )}
                      {l.type === "changed" && (
                        <>
                          <span className="text-[10.5px] font-semibold uppercase tracking-wide text-amber-600 bg-amber-500/10 rounded px-1.5 py-0.5 mt-px shrink-0">Change</span>
                          <span>{l.act.name}
                            <span className="text-text-muted"> · {l.changes.join(" · ")}</span>
                          </span>
                        </>
                      )}
                      {l.type === "removed" && (
                        <>
                          <span className="text-[10.5px] font-semibold uppercase tracking-wide text-red-600 bg-red-500/10 rounded px-1.5 py-0.5 mt-px shrink-0">Gone</span>
                          <span className="text-text-muted line-through">{l.act.name}</span>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {hazards.length > 0 && (
                <div className="border border-danger/40 bg-danger/5 rounded-lg px-3 py-2.5">
                  <div className="flex items-center gap-1.5 text-[12.5px] font-medium text-danger mb-1">
                    <AlertTriangle size={13} /> {hazards.length === 1 ? "One line is" : `${hazards.length} lines are`} keeping sub-tasks from the previous revision
                  </div>
                  <div className="text-[12px] text-text-muted mb-2">
                    These ids now hold a different activity, and the payload didn’t mention <span className="font-mono">subs</span> — so the old
                    tick-boxes stay attached. Sub-tasks put a line on the crew’s build plan, so they’d show up in Today’s Work
                    and My Schedule on someone’s phone.
                  </div>
                  <ul className="text-[12.5px] space-y-1 mb-2">
                    {hazards.map((h) => (
                      <li key={h.code} className="leading-snug">
                        <span className="font-mono text-[11.5px] text-text-muted">{h.code}</span>{" "}
                        <span className="font-medium">{h.name}</span>
                        {h.milestone && <span className="text-text-muted"> ({h.kind} — should never carry tick-boxes)</span>}
                        <div className="text-text-muted">
                          was “{h.wasName}” · keeping {h.subCount} sub-task{h.subCount === 1 ? "" : "s"} from it
                        </div>
                      </li>
                    ))}
                  </ul>
                  <label className="flex items-start gap-2 text-[12.5px]">
                    <input type="checkbox" className="mt-0.5" checked={clearStale} onChange={(e) => setClearStale(e.target.checked)} />
                    <span>
                      Clear those sub-tasks as part of this import <span className="text-text-muted">(recommended — untick only if
                      you meant the steps to carry across)</span>
                    </span>
                  </label>
                </div>
              )}

              {bookingImpacts.length > 0 && (
                <div className="border border-amber-500/40 bg-amber-500/5 rounded-lg px-3 py-2.5">
                  <div className="flex items-center gap-1.5 text-[12.5px] font-medium text-amber-700 mb-1">
                    <AlertTriangle size={13} /> Bookings sitting in changed windows — check these after applying
                  </div>
                  <ul className="text-[12.5px] text-text-muted space-y-0.5">
                    {bookingImpacts.map((b, i) => (
                      <li key={i}>{b.title} on {b.window} — inside “{b.hit}”</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex items-center justify-between pt-1">
                <Button variant="ghost" size="sm" onClick={() => { setPayload(null); setBookingImpacts([]); }}>
                  Back
                </Button>
                <div className="flex items-center gap-2">
                  <span className="text-[12px] text-text-muted">Applies atomically — all or nothing</span>
                  <Button size="sm" icon={applying ? undefined : Check} onClick={apply} disabled={applying}>
                    {applying ? "Applying…" : "Apply program"}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// keep icons referenced so tree-shake-lint stays quiet if unused paths change
void ChevronRight; void ChevronDown;
